import type React from "react";
import { createAnimationGraphNode } from "@/document/model/animation";
import { findSceneNode, getActivePageSceneNodes, getAllSceneNodes, type CurvaDocument, type PrototypeLink } from "@/document/model/document";
import { editableNodeIds, isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { nodeSelectionDragDeltaToLocal, nodeSelectionPointToLocal } from "@/document/operations/nodeWorldTransform";
import { sampleEdgeBezier } from "@/document/operations/vectorGeometry";
import { snapPointToVertex } from "@/document/operations/vectorVertexSnap";
import { handlesAtVertex, vectorHandleUpdatesForMove } from "@/document/operations/vectorHandleMirroring";
import { hitTestVectorRegion } from "@/document/operations/vectorRegions";
import { hitTestShapeBuilderRegion } from "@/document/operations/shapeBuilder";
import type { SceneNode } from "@/document/model/nodes";
import {
  getNodesInMarquee,
  getNodesInLasso,
  hitTestResizeHandle,
  hitTestSelectionRotationHandle,
  hitTestDocument,
  normalizeRect,
  resizeBounds,
  transformBoundsInGroup,
} from "@/editor/tools/hitTest";
import { visibleObjectSnapCandidates, type ObjectSnapGuide } from "@/editor/tools/objectSnap";
import { pointerInHorizontalRuler, pointerInVerticalRuler } from "@/editor/tools/guideHitTest";
import { guideScopesEqual } from "@/document/model/guides";
import {
  createPencilTraceSession,
  imageFillForPencilTrace,
  snapPointToPencilTrace,
  type PencilTraceSession,
} from "@/editor/tools/pencilTrace";
import { snapVectorPointToGeometry, vectorEditGuidesToWorld } from "@/editor/tools/vectorGeometrySnap";
import { applySculptSegment, initialSculptPositions } from "@/editor/tools/sculptBrush";
import { cancelSubdivideSession } from "@/editor/tools/subdivideSession";
import { constrainExtrudeDragDelta, extrudeEdges } from "@/document/operations/vectorExtrude";
import {
  bentEdgeHandles,
  hitTestVectorEdge,
  hitTestVectorHandle,
  hitTestVectorIntersection,
  hitTestVectorVertex,
  insertionForEdge,
  previewPointForEdge,
  resolveVectorAreaSelection,
} from "@/editor/tools/vectorEditHitTest";
import {
  hitTestGradientGizmo,
  paintWithGradientEndpoint,
  paintWithGradientStopPosition,
  paintWithRadialCenter,
  paintWithRadialRadiusPoint,
  paintWithRadialStopPosition,
  stopPositionForPoint,
  type GradientGizmoHandle,
} from "@/editor/tools/gradientGizmo";
import {
  hitTestImageFillGizmo,
  imagePaintWithMovedCrop,
  imagePaintWithResizedCrop,
} from "@/editor/tools/imageFillGizmo";
import {
  hitTestPrimitiveGizmo,
  ovalWithArcEndDrag,
  ovalWithArcStartDrag,
  ovalWithRatioDrag,
  polygonWithCountDrag,
  polygonWithCurvatureDrag,
  starWithCountDrag,
  starWithRatioDrag,
} from "@/editor/tools/primitiveGizmo";
import { screenToDocument } from "@/editor/state/viewportStore";
import type { Rect } from "@/lib/math/rect";
import { inverseRotatePoint, normalizeDegrees, rectCenter, rotatePoint, transformRotationOrigin } from "@/lib/math/rotation";
import { documentStore, selectionStore, uiStore, viewStore } from "@/editor/store";
import { clearPointerHoverState, viewportBoundsForHitTest } from "@/editor/shell/pointerHover";
import { frameLabelOverlays, hitTestFrameLabel, hitTestFrameLabelScriptAction } from "@/editor/shell/frameLabels";
import { textIndexAtPoint } from "@/renderer/text/canvasKitText";
import { sampleRenderedCanvas } from "@/editor/shell/colorSampler";
import {
  applySelectionClick,
  applySelectionPointerDown,
  animationFrameDragModeForPoint,
  canvasViewportBounds,
  constrainResizeBoundsAspectRatioAroundCenter,
  constrainResizeBoundsAspectRatio,
  deepSelectModifier,
  dragOffsetFromPreview,
  findDeepestFrameAtPoint,
  findTopFrameAtPoint,
  flexReorderPreviewForDrag,
  hitTestRotationOrigin,
  hitTestVectorSmartTransform,
  isDrawableBounds,
  mergeSelection,
  movePreviewsForDrag,
  pointInBounds,
  resizeBoundsAroundCenter,
  rotationOriginSnapPoints,
  selectionBoundsForTransforms,
  selectionRotationForNodes,
  selectionRotationOriginForNodes,
  shapeDrawBounds,
  snapBoundsOriginToPixelGrid,
  snapShapeDrawBoundsToFrame,
  snapDegrees,
  snapPointToAngle,
  snapPointToPixelGrid,
  snapToPixelGridValue,
  snapRotationOrigin,
  toggleEdgeSelection,
  toggleVertexSelection,
  transformPointBetweenRotatedBounds,
  vectorEditLocalPoint,
} from "@/editor/shell/canvasGeometry";
import { setTextareaSelection } from "@/editor/shell/canvasTextEditing";
import {
  beginPointerCancelTrace,
  beginPointerDownTrace,
  beginPointerMoveTrace,
  beginPointerUpTrace,
  endPointerCancelTrace,
  endPointerDownTrace,
  endPointerMoveTrace,
  endPointerUpTrace,
  timeDebugStep,
} from "@/renderer/renderDebug";
import {
  guideDocumentPositionFromPointer,
  guidePositionForScope,
  pointInsideCanvasContentArea,
  resolveGuidePlacement,
  resolveGuideScopeAtPoint,
} from "@/editor/shell/guideInteraction";
import { animationNodesInRect, nearestAnimationObjectInputPin } from "@/editor/shell/AnimationGraphOverlay";
import { topFrameIdAtPoint } from "@/editor/shell/shellSelection";
import type { ActiveTool } from "@/editor/shell/TopBar";
import type { DragState, FrameLabelOverlay } from "@/editor/shell/canvasWorkspaceTypes";
import type { PointerRouter, PointerRouterHost } from "./pointerRouterTypes";
import { animationTargetsFromVectorSelection } from "@/editor/shell/animationTargetFromSelection";


function hitTestPrototypeLink(
  document: CurvaDocument,
  links: PrototypeLink[],
  point: { x: number; y: number },
  zoom: number,
): PrototypeLink | undefined {
  const tolerance = 8 / Math.max(0.0001, zoom);
  return links
    .slice()
    .reverse()
    .find(link => {
      const source = findSceneNode(document, link.sourceNodeId);
      const target = findSceneNode(document, link.targetFrameId);
      if (!source || target?.type !== "frame") {
        return false;
      }
      return distanceToSegment(point, nodeCenter(source), nodeCenter(target)) <= tolerance;
    });
}

function hitTestPrototypeAnchor(
  document: CurvaDocument,
  point: { x: number; y: number },
  zoom: number,
  activePageId?: string,
): { node: SceneNode } | undefined {
  const radius = 14 / Math.max(0.0001, zoom);
  const nodes = getActivePageSceneNodes(document, activePageId)
    .filter(node => node.visible !== false && !node.locked)
    .slice()
    .reverse();
  for (const node of nodes) {
    const center = nodeCenter(node);
    if (Math.hypot(point.x - center.x, point.y - center.y) <= radius) {
      return { node };
    }
  }
  return undefined;
}

function nodeCenter(node: SceneNode): { x: number; y: number } {
  return {
    x: node.bounds.x + node.bounds.width / 2,
    y: node.bounds.y + node.bounds.height / 2,
  };
}

function distanceToSegment(
  point: { x: number; y: number },
  start: { x: number; y: number },
  end: { x: number; y: number },
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= 0.000001) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (start.x + dx * t), point.y - (start.y + dy * t));
}

function pencilTraceSessionAtPoint(
  document: CurvaDocument,
  point: { x: number; y: number },
  selectionBounds: Rect | undefined,
  zoom: number,
  selectedNodeIds: string[],
  event: { metaKey: boolean; ctrlKey: boolean },
  activePageId?: string,
): PencilTraceSession | undefined {
  const hit = hitTestDocument(document, point, selectionBounds, zoom, {
    activePageId,
    deepSelect: deepSelectModifier(event),
    selectedNodeIds,
  });
  if (hit.type !== "node") {
    return undefined;
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === hit.nodeId);
  const imageFill = imageFillForPencilTrace(node);
  return node && imageFill ? createPencilTraceSession(node, imageFill) : undefined;
}

function pencilPointForTrace(
  point: { x: number; y: number },
  trace: PencilTraceSession | undefined,
  snapToPixelGrid: boolean,
  zoom: number,
): { x: number; y: number } {
  return snapPointToPencilTrace(trace?.edgeMap, point, zoom)
    ?? (snapToPixelGrid ? snapPointToPixelGrid(point) : point);
}

function verticesForEdges(node: Extract<SceneNode, { type: "vectorNetwork" }>, edgeIds: string[]): Array<{ vertexId: string; x: number; y: number }> {
  const selectedEdges = new Set(edgeIds);
  const vertexIds: string[] = [];
  for (const edge of node.network.edges) {
    if (!selectedEdges.has(edge.id)) {
      continue;
    }
    for (const vertexId of [edge.from.vertexId, edge.to.vertexId]) {
      if (!vertexIds.includes(vertexId)) {
        vertexIds.push(vertexId);
      }
    }
  }

  return vertexIds.flatMap(vertexId => {
    const vertex = node.network.vertices.find(candidate => candidate.id === vertexId);
    return vertex ? [{ vertexId, x: vertex.x, y: vertex.y }] : [];
  });
}

function rotateHandlesForVertices(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  vertexIds: string[],
  center: { x: number; y: number },
  degrees: number,
): Array<{ edgeId: string; endpoint: "from" | "to"; x: number; y: number }> {
  const selectedVertices = new Set(vertexIds);
  return node.network.edges.flatMap(edge => {
    const handles: Array<{ edgeId: string; endpoint: "from" | "to"; x: number; y: number }> = [];
    if (edge.from.handle && selectedVertices.has(edge.from.vertexId)) {
      handles.push({ edgeId: edge.id, endpoint: "from", ...rotatePoint(edge.from.handle, center, degrees) });
    }
    if (edge.to.handle && selectedVertices.has(edge.to.vertexId)) {
      handles.push({ edgeId: edge.id, endpoint: "to", ...rotatePoint(edge.to.handle, center, degrees) });
    }
    return handles;
  });
}

function knifeLineCuts(document: CurvaDocument, nodeId: string, from: { x: number; y: number }, to: { x: number; y: number }): Array<{ edgeId: string; point: { x: number; y: number }; t: number }> {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return [];
  }
  const vertexById = new Map(node.network.vertices.map(vertex => [vertex.id, vertex]));
  return node.network.edges.flatMap(edge => {
    const samples = sampleEdgeForKnife(vertexById, edge, 32);
    let best: { edgeId: string; point: { x: number; y: number }; t: number; lineT: number } | undefined;
    for (let index = 0; index < samples.length - 1; index += 1) {
      const hit = segmentIntersectionForKnife(from, to, samples[index]!, samples[index + 1]!);
      if (!hit || hit.firstT <= 0.0001 || hit.firstT >= 0.9999 || hit.secondT <= 0.0001 || hit.secondT >= 0.9999) {
        continue;
      }
      const t = (index + hit.secondT) / Math.max(1, samples.length - 1);
      if (!best || hit.firstT < best.lineT) {
        best = { edgeId: edge.id, point: hit.point, t, lineT: hit.firstT };
      }
    }
    return best ? [{ edgeId: best.edgeId, point: best.point, t: best.t }] : [];
  });
}

function knifeAroundEdgeIds(document: CurvaDocument, nodeId: string, vertexId: string | undefined, path: Array<{ x: number; y: number }>): string[] {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork" || !vertexId || path.length < 2) {
    return [];
  }
  const vertexById = new Map(node.network.vertices.map(vertex => [vertex.id, vertex]));
  return node.network.edges
    .filter(edge => edge.from.vertexId === vertexId || edge.to.vertexId === vertexId)
    .filter(edge => polylinesIntersect(path, sampleEdgeForKnife(vertexById, edge, 32)))
    .map(edge => edge.id);
}

function sampleEdgeForKnife(
  vertexById: Map<string, { x: number; y: number }>,
  edge: { from: { vertexId: string; handle?: { x: number; y: number } }; to: { vertexId: string; handle?: { x: number; y: number } } },
  steps: number,
): Array<{ x: number; y: number }> {
  const from = vertexById.get(edge.from.vertexId);
  const to = vertexById.get(edge.to.vertexId);
  if (!from || !to) {
    return [];
  }
  return sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, steps);
}

function polylinesIntersect(left: Array<{ x: number; y: number }>, right: Array<{ x: number; y: number }>): boolean {
  for (let leftIndex = 0; leftIndex < left.length - 1; leftIndex += 1) {
    for (let rightIndex = 0; rightIndex < right.length - 1; rightIndex += 1) {
      if (segmentIntersectionForKnife(left[leftIndex]!, left[leftIndex + 1]!, right[rightIndex]!, right[rightIndex + 1]!)) {
        return true;
      }
    }
  }
  return false;
}

function segmentIntersectionForKnife(
  firstFrom: { x: number; y: number },
  firstTo: { x: number; y: number },
  secondFrom: { x: number; y: number },
  secondTo: { x: number; y: number },
): { point: { x: number; y: number }; firstT: number; secondT: number } | undefined {
  const firstDx = firstTo.x - firstFrom.x;
  const firstDy = firstTo.y - firstFrom.y;
  const secondDx = secondTo.x - secondFrom.x;
  const secondDy = secondTo.y - secondFrom.y;
  const denominator = firstDx * secondDy - firstDy * secondDx;
  if (Math.abs(denominator) <= 1e-9) {
    return undefined;
  }
  const deltaX = secondFrom.x - firstFrom.x;
  const deltaY = secondFrom.y - firstFrom.y;
  const firstT = (deltaX * secondDy - deltaY * secondDx) / denominator;
  const secondT = (deltaX * firstDy - deltaY * firstDx) / denominator;
  if (firstT < 0 || firstT > 1 || secondT < 0 || secondT > 1) {
    return undefined;
  }
  return { point: { x: firstFrom.x + firstDx * firstT, y: firstFrom.y + firstDy * firstT }, firstT, secondT };
}

function findVectorVertex(document: CurvaDocument, nodeId: string, vertexId: string): { id: string; x: number; y: number } | undefined {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  return node?.type === "vectorNetwork" ? node.network.vertices.find(vertex => vertex.id === vertexId) : undefined;
}

function pathPreviewSegmentForCursor(
  document: CurvaDocument,
  nodeId: string,
  selectedVertexId: string | undefined,
  point: { x: number; y: number },
  activeTool: ActiveTool,
  constrained: boolean,
  snapToPixelGrid: boolean,
): { nodeId: string; from: { x: number; y: number }; to: { x: number; y: number } } | undefined {
  if (activeTool !== "path" || !selectedVertexId) {
    return undefined;
  }

  const from = findVectorVertex(document, nodeId, selectedVertexId);
  if (!from || Math.hypot(from.x - point.x, from.y - point.y) <= 0.0001) {
    return undefined;
  }

  const targetPoint = snapToPixelGrid ? snapPointToPixelGrid(point) : point;
  return {
    nodeId,
    from,
    to: snapToPixelGrid
      ? snapPointToPixelGrid(constrained ? snapPointToAngle(from, targetPoint) : targetPoint)
      : constrained
        ? snapPointToAngle(from, point)
        : point,
  };
}

function penDragHandles(
  edge: {
    from: { x: number; y: number };
    to: { x: number; y: number };
  },
  dragPoint: { x: number; y: number },
  fromIncomingHandle?: { x: number; y: number },
): { fromHandle: { x: number; y: number }; toHandle: { x: number; y: number } } {
  const dx = dragPoint.x - edge.to.x;
  const dy = dragPoint.y - edge.to.y;
  // The outgoing handle at the segment's start vertex should preserve the tangent
  // established when that vertex was placed, i.e. mirror its existing incoming
  // handle (angle + length). Only fall back to a baseline third-point handle when
  // the vertex has no handle yet (e.g. drawing out of a plain corner click).
  const fromHandle = fromIncomingHandle
    ? { x: 2 * edge.from.x - fromIncomingHandle.x, y: 2 * edge.from.y - fromIncomingHandle.y }
    : lerpPoint(edge.from, edge.to, 1 / 3);
  return {
    fromHandle,
    toHandle: {
      x: edge.to.x - dx,
      y: edge.to.y - dy,
    },
  };
}

function lerpPoint(from: { x: number; y: number }, to: { x: number; y: number }, t: number): { x: number; y: number } {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

function toggleSelectionSet(current: string[], toggled: string[]): string[] {
  const toggledSet = new Set(toggled);
  const next = current.filter(id => !toggledSet.has(id));
  for (const id of toggled) {
    if (!current.includes(id)) {
      next.push(id);
    }
  }
  return next;
}

function applyVectorAreaSelection(
  host: PointerRouterHost,
  hit: { kind: "vertices" | "edges"; ids: string[] },
  shiftKey: boolean,
  additiveMode: "merge" | "toggle",
): void {
  if (hit.kind === "vertices") {
    const next = shiftKey
      ? (additiveMode === "toggle" ? toggleSelectionSet(host.selectedVertexIds, hit.ids) : mergeSelection(host.selectedVertexIds, hit.ids))
      : hit.ids;
    host.vectorActions.onSelectedVerticesChange(next);
    return;
  }

  const next = shiftKey
    ? (additiveMode === "toggle" ? toggleSelectionSet(host.selectedEdgeIds, hit.ids) : mergeSelection(host.selectedEdgeIds, hit.ids))
    : hit.ids;
  host.vectorActions.onSelectedEdgesChange(next);
}

function gradientHandleKey(handle: GradientGizmoHandle): string {
  return handle.type === "stop" ? handle.stopId : handle.type;
}

export function createPointerRouter(host: PointerRouterHost): PointerRouter {
  function frameLabelViewportBounds() {
    const canvas = host.canvasRef.current;
    return canvas ? viewportBoundsForHitTest(host.cameraRef.current, canvas) : undefined;
  }

  function timedSelectionChange(nodeIds: string[], primaryNodeId?: string): void {
    timeDebugStep("onSelectionChange", "input", `${nodeIds.length} nodes`, () => {
      host.documentActions.onSelectionChange(nodeIds, primaryNodeId);
    });
  }

function handlePointerDownImpl(event: React.PointerEvent<HTMLCanvasElement>): void {
  if (!host.canvasRef.current || event.button !== 0) {
    return;
  }

  if (host.spacePanModifierRef.current && !host.colorSampler) {
    host.closePanelFloatingUi();
    event.currentTarget.setPointerCapture(event.pointerId);
    clearPointerHover();
    host.dragRef.current = {
      mode: "pan",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: host.cameraRef.current.x,
      startDocumentY: host.cameraRef.current.y,
      startBoundsById: new Map(),
      nodeIds: [],
      moved: false,
    };
    host.syncCanvasCursor();
    return;
  }

  if (host.colorSampler) {
    host.sampledPointerIdRef.current = event.pointerId;
    const overlay = host.buildRenderOverlayForWorkspace(host.cameraRef.current);
    host.renderOverlayRef.current = overlay;
    host.rendererRef.current?.drawImmediate(host.documentRef.current, overlay, "full");
    const sample = sampleRenderedCanvas(host.canvasRef.current, event.clientX, event.clientY);
    if (sample) {
      host.documentActions.onColorSample(sample.color, { sampleStroke: event.shiftKey });
      host.setColorSamplePreview(undefined);
    }
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  const frameLabelHit = hitTestFrameLabel(
    frameLabelOverlays(documentStore.getDocument(), host.cameraRef.current, host.previews, host.resolvedActivePageId, frameLabelViewportBounds(), {
      allowScripts: uiStore.allowScripts.getValue(),
    }),
    event.clientX,
    event.clientY,
  );
  if (frameLabelHit) {
    handleFrameLabelPointerDown(event, frameLabelHit);
    return;
  }

  const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
  const snappedPoint = host.snapToPixelGrid ? snapPointToPixelGrid(point) : point;
  const continuesDoubleClickSequence = updateDoubleClickBaseGroup(event);
  clearPointerHover();

  if (host.activeTool === "lasso" && host.animationModeActive && host.vectorTargetNodeId) {
    event.currentTarget.setPointerCapture(event.pointerId);
    host.dragRef.current = {
      mode: "lasso",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: new Map(),
      nodeIds: [host.vectorTargetNodeId],
      lassoPoints: [snappedPoint],
      moved: false,
    };
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (host.animationModeActive && host.vectorTargetNodeId) {
    const targetNodeId = host.vectorTargetNodeId;
    const targetNode = findSceneNode(documentStore.getDocument(), targetNodeId);
    if (targetNode?.type === "vectorNetwork") {
      event.currentTarget.setPointerCapture(event.pointerId);
      const vertexHit = hitTestVectorVertex(documentStore.getDocument(), targetNodeId, point, host.cameraRef.current.zoom);
      const handleHit = !vertexHit ? hitTestVectorHandle(documentStore.getDocument(), targetNodeId, point, host.cameraRef.current.zoom) : undefined;
      const edgeHit = (!vertexHit && !handleHit) ? hitTestVectorEdge(documentStore.getDocument(), targetNodeId, point, host.cameraRef.current.zoom) : undefined;

      if (vertexHit) {
        const vertexId = vertexHit.id;
        const nextIds = event.shiftKey
          ? host.selectedVertexIds.includes(vertexId)
            ? host.selectedVertexIds.filter(id => id !== vertexId)
            : [...host.selectedVertexIds, vertexId]
          : host.selectedVertexIds.includes(vertexId)
            ? host.selectedVertexIds
            : [vertexId];
        host.vectorActions.onSelectedVerticesChange(nextIds);
        host.dragRef.current = {
          mode: "animationObjectLink",
          startClientX: event.clientX,
          startClientY: event.clientY,
          startDocumentX: snappedPoint.x,
          startDocumentY: snappedPoint.y,
          startBoundsById: new Map(),
          nodeIds: [targetNodeId],
          animationSourceNodeId: targetNodeId,
          moved: false,
        };
        return;
      }
      if (handleHit) {
        host.vectorActions.onSelectedHandleChange({ edgeId: handleHit.edgeId, endpoint: handleHit.endpoint });
        host.dragRef.current = {
          mode: "animationObjectLink",
          startClientX: event.clientX,
          startClientY: event.clientY,
          startDocumentX: snappedPoint.x,
          startDocumentY: snappedPoint.y,
          startBoundsById: new Map(),
          nodeIds: [targetNodeId],
          animationSourceNodeId: targetNodeId,
          moved: false,
        };
        return;
      }
      if (edgeHit) {
        const nextEdgeIds = event.shiftKey
          ? host.selectedEdgeIds.includes(edgeHit.edgeId)
            ? host.selectedEdgeIds.filter(id => id !== edgeHit.edgeId)
            : [...host.selectedEdgeIds, edgeHit.edgeId]
          : host.selectedEdgeIds.includes(edgeHit.edgeId)
            ? host.selectedEdgeIds
            : [edgeHit.edgeId];
        host.vectorActions.onSelectedEdgesChange(nextEdgeIds);
        host.dragRef.current = {
          mode: "animationObjectLink",
          startClientX: event.clientX,
          startClientY: event.clientY,
          startDocumentX: snappedPoint.x,
          startDocumentY: snappedPoint.y,
          startBoundsById: new Map(),
          nodeIds: [targetNodeId],
          animationSourceNodeId: targetNodeId,
          moved: false,
        };
        return;
      }
      if (!event.shiftKey) {
        host.vectorActions.onSelectedVerticesChange([]);
      }
      host.dragRef.current = {
        mode: "animationMarquee",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [],
        moved: false,
      };
      return;
    }
  }

  if (host.animationModeActive && host.armedAnimationNodeKind && host.animationActions.onAnimationNodeInsert) {
    event.preventDefault();
    event.stopPropagation();
    const frameId = host.armedAnimationNodeKind === "animation" ? topFrameIdAtPoint(documentStore.getDocument(), point, host.resolvedActivePageId) : undefined;
    host.animationActions.onAnimationNodeInsert(createAnimationGraphNode(host.armedAnimationNodeKind, point, { frameId }));
    host.animationActions.onAnimationNodeArm?.(undefined);
    return;
  }

  if (host.animationModeActive && host.mode === "select") {
    const containingFrame = findTopFrameAtPoint(documentStore.getDocument(), point, host.resolvedActivePageId);
    const hit = hitTestDocument(documentStore.getDocument(), point, host.selectionTransformBounds, host.cameraRef.current.zoom, host.hitTestOptions({
      deepSelect: deepSelectModifier(event),
      selectedNodeIds: host.editableSelectedNodeIds,
    }));
    const hitNode = hit.type === "node" ? findSceneNode(documentStore.getDocument(), hit.nodeId) : undefined;
    if (hitNode?.type === "frame" || (containingFrame && !hitNode)) {
      const sourceFrame = hitNode?.type === "frame" ? hitNode : containingFrame;
      event.currentTarget.setPointerCapture(event.pointerId);
      host.dragRef.current = {
        mode: "marquee",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: sourceFrame ? [sourceFrame.id] : [],
        animationSourceNodeId: sourceFrame?.id,
        moved: false,
      };
      host.syncCanvasCursor();
      return;
    }
    if (hitNode && host.animationActions.onAnimationObjectConnect) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const nextSelection = applySelectionPointerDown(host.selectedNodeIds, hitNode.id, event.shiftKey);
      host.documentActions.onSelectionChange(nextSelection, nextSelection.includes(hitNode.id) ? hitNode.id : nextSelection.at(-1));
      host.setPrototypeLinkPreview({ from: nodeCenter(hitNode), to: point });
      host.dragRef.current = {
        mode: "animationObjectLink",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [hitNode.id],
        animationSourceNodeId: hitNode.id,
        moved: false,
      };
      return;
    }
    if (host.animationActions.onAnimationNodeSelectionChange) {
      if (!event.shiftKey) {
        host.documentActions.onSelectionChange([], undefined);
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      host.dragRef.current = {
        mode: "animationMarquee",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [],
        moved: false,
      };
      host.syncCanvasCursor();
      return;
    }
  }

  if (host.devModeActive) {
    // Dev host.mode is read-only: only allow selection clicks, no dragging or editing.
    const hit = hitTestDocument(documentStore.getDocument(), point, host.selectionTransformBounds, host.cameraRef.current.zoom, host.hitTestOptions({
      deepSelect: deepSelectModifier(event),
      selectedNodeIds: host.editableSelectedNodeIds,
    }));
    if (hit.type === "node") {
      const nextSelection = applySelectionPointerDown(host.selectedNodeIds, hit.nodeId, event.shiftKey);
      host.documentActions.onSelectionChange(nextSelection, nextSelection.includes(hit.nodeId) ? hit.nodeId : nextSelection.at(-1));
      host.syncOverlayRender({ selectedNodeIds: editableNodeIds(documentStore.getDocument(), nextSelection) }, { immediate: true });
    } else if (!event.shiftKey) {
      host.documentActions.onSelectionChange([], undefined);
    }
    return;
  }

  if (host.prototypeModeActive && host.mode === "select") {
    const prototypeLinkHit = hitTestPrototypeLink(documentStore.getDocument(), host.prototypeLinksList, point, host.cameraRef.current.zoom);
    if (prototypeLinkHit) {
      host.documentActions.onSelectionChange([], undefined);
      host.documentActions.onPrototypeLinkSelect(prototypeLinkHit.id);
      return;
    }

    const prototypeHit = hitTestPrototypeAnchor(documentStore.getDocument(), point, host.cameraRef.current.zoom, host.resolvedActivePageId);
    if (prototypeHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const center = nodeCenter(prototypeHit.node);
      host.setPrototypeLinkPreview({ from: center, to: point });
      host.setPointerHover({ hoveredPrototypeNodeId: prototypeHit.node.id, hoveredNodeId: prototypeHit.node.id });
      host.dragRef.current = {
        mode: "prototypeLink",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [prototypeHit.node.id],
        prototypeSourceNodeId: prototypeHit.node.id,
        moved: false,
      };
      return;
    }
  }

  if (host.mode === "select" && host.activeTool === "select" && host.beginGuidePointerDown(event, point)) {
    return;
  }

  if ((host.activeTool === "shape" || host.activeTool === "frame") && host.mode === "select") {
    event.currentTarget.setPointerCapture(event.pointerId);
    host.setDraftShape(undefined);
    host.dragRef.current = {
      mode: "draw",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: new Map(),
      nodeIds: [],
      shapeKind: host.activeTool === "frame" ? "frame" : host.selectedShapeKind,
      moved: false,
    };
    return;
  }

  if (host.activeTool === "text" && host.mode === "select") {
    event.currentTarget.setPointerCapture(event.pointerId);
    host.dragRef.current = {
      mode: "textDraw",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: new Map(),
      nodeIds: [],
      moved: false,
    };
    return;
  }

  if (host.activeTool === "select" && host.mode === "textEdit" && host.selectedTextNode && pointInBounds(point, host.selectedTextNode.bounds)) {
    const index = textIndexAtPoint(host.selectedTextNode, point, Math.round(host.cameraRef.current.zoom * 10));
    event.currentTarget.setPointerCapture(event.pointerId);
    host.textInputRef.current?.focus();
    host.textInputRef.current?.setSelectionRange(index, index);
    host.updateTextSelection({ anchor: index, focus: index });
    host.dragRef.current = {
      mode: "textSelect",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: new Map(),
      nodeIds: [host.selectedTextNode.id],
      startTextIndex: index,
      moved: false,
    };
    return;
  }

  if (host.activeTool === "pencil") {
    event.currentTarget.setPointerCapture(event.pointerId);
    host.clearDraftPencilRender();
    const pencilTrace = host.assistedPencilTracing
      ? pencilTraceSessionAtPoint(documentStore.getDocument(), point, host.selectionBounds, host.cameraRef.current.zoom, host.editableSelectedNodeIds, event, host.resolvedActivePageId)
      : undefined;
    const firstPoint = pencilPointForTrace(point, pencilTrace, host.snapToPixelGrid, host.cameraRef.current.zoom);
    host.dragRef.current = {
      mode: "pencil",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: firstPoint.x,
      startDocumentY: firstPoint.y,
      startBoundsById: new Map(),
      nodeIds: [],
      pencilPoints: [firstPoint],
      pencilRawPoints: [point],
      pencilTrace,
      moved: false,
    };
    if (pencilTrace) {
      void pencilTrace.ready.then(edgeMap => {
        const drag = host.dragRef.current;
        if (!edgeMap || drag?.mode !== "pencil" || drag.pencilTrace?.id !== pencilTrace.id) {
          return;
        }

        pencilTrace.edgeMap = edgeMap;
        const rawPoints = drag.pencilRawPoints ?? drag.pencilPoints ?? [];
        drag.pencilPoints = rawPoints.map(rawPoint => pencilPointForTrace(rawPoint, pencilTrace, host.snapToPixelGrid, host.cameraRef.current.zoom));
        const first = drag.pencilPoints[0];
        if (first) {
          drag.startDocumentX = first.x;
          drag.startDocumentY = first.y;
        }
        host.scheduleDraftPencilRender(drag.pencilPoints);
      });
    }
    return;
  }

  if (host.activeTool === "path" && host.mode === "select") {
    const started = host.vectorActions.onPathToolStart(snappedPoint);
    if (started) {
      event.currentTarget.setPointerCapture(event.pointerId);
      host.dragRef.current = {
        mode: "pathInsert",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [started.nodeId],
        vertexId: started.vertexId,
        moved: false,
      };
    }
    return;
  }

  if (host.activeTool === "lasso" && (host.mode === "select" || host.mode === "vectorEdit")) {
    event.currentTarget.setPointerCapture(event.pointerId);
    host.dragRef.current = {
      mode: "lasso",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: new Map(),
      nodeIds: host.primarySelectedNodeId ? [host.primarySelectedNodeId] : [],
      lassoPoints: [snappedPoint],
      moved: false,
    };
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (host.mode === "vectorEdit" && host.primarySelectedNodeId) {
    const vectorPoint = vectorEditLocalPoint(documentStore.getDocument(), host.primarySelectedNodeId, point);
    const snappedVectorPoint = host.snapToPixelGrid ? snapPointToPixelGrid(vectorPoint) : vectorPoint;
    if (host.activeTool === "subdivide") {
      cancelSubdivideSession(true);
      return;
    }
    if (host.activeTool === "extrude") {
      const edgeHit = hitTestVectorEdge(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
      if (edgeHit) {
        event.currentTarget.setPointerCapture(event.pointerId);
        const nextEdgeIds = event.shiftKey
          ? toggleEdgeSelection(host.selectedEdgeIds, edgeHit.edgeId, true)
          : host.selectedEdgeIds.includes(edgeHit.edgeId)
            ? host.selectedEdgeIds
            : [edgeHit.edgeId];
        host.vectorActions.onSelectedEdgesChange(nextEdgeIds);
        host.dragRef.current = {
          mode: "extrude",
          startClientX: event.clientX,
          startClientY: event.clientY,
          startDocumentX: snappedVectorPoint.x,
          startDocumentY: snappedVectorPoint.y,
          startBoundsById: new Map(),
          nodeIds: [host.primarySelectedNodeId],
          edgeIds: nextEdgeIds,
          edgeId: edgeHit.edgeId,
          moved: false,
        };
        return;
      }

      const nodeHit = hitTestDocument(documentStore.getDocument(), point, undefined, host.cameraRef.current.zoom, host.hitTestOptions({
        deepSelect: deepSelectModifier(event),
        selectedNodeIds: host.editableSelectedNodeIds,
      }));
      event.currentTarget.setPointerCapture(event.pointerId);
      host.dragRef.current = {
        mode: "vectorMarquee",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        exitVectorModeOnClick: nodeHit.type !== "node" || nodeHit.nodeId !== host.primarySelectedNodeId,
        moved: false,
      };
      return;
    }
    if (host.activeTool === "sculpt") {
      const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === host.primarySelectedNodeId);
      if (node?.type !== "vectorNetwork") {
        return;
      }
      const brush = uiStore.sculptBrush.getValue();
      const radius = brush.size / 2;
      event.currentTarget.setPointerCapture(event.pointerId);
      host.dragRef.current = {
        mode: "sculpt",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: vectorPoint.x,
        startDocumentY: vectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [node.id],
        startVertices: node.network.vertices.map(vertex => ({ vertexId: vertex.id, x: vertex.x, y: vertex.y })),
        sculptPositions: initialSculptPositions(node.network),
        sculptLastSample: { x: vectorPoint.x, y: vectorPoint.y },
        sculptRadius: radius,
        sculptStrength: brush.strength,
        moved: false,
      };
      viewStore.setSculptBrushPreview({ nodeId: node.id, x: vectorPoint.x, y: vectorPoint.y, radius });
      return;
    }
    if (host.activeTool === "shapeBuilder") {
      event.currentTarget.setPointerCapture(event.pointerId);
      const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === host.primarySelectedNodeId);
      const region = node?.type === "vectorNetwork" ? hitTestShapeBuilderRegion(node.network, vectorPoint) : undefined;
      const shapeBuilderMode = event.altKey ? "remove" : "add";
      if (region) {
        host.applyShapeBuilderRegionSelection(region.id, shapeBuilderMode);
        host.setShapeBuilderHoverRegionId(region.id);
      }
      host.dragRef.current = {
        mode: "shapeBuilder",
        shapeBuilderMode,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        moved: false,
      };
      return;
    }

    if (host.activeTool === "fill") {
      event.currentTarget.setPointerCapture(event.pointerId);
      const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === host.primarySelectedNodeId);
      const region = node?.type === "vectorNetwork" ? hitTestVectorRegion(node.network, vectorPoint) : undefined;
      if (region) {
        host.vectorActions.onVectorRegionFillToggle(host.primarySelectedNodeId, region.region.edgeIds);
      }
      host.dragRef.current = {
        mode: "regionFill",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        moved: false,
      };
      return;
    }

    if (host.activeTool === "knife") {
      event.currentTarget.setPointerCapture(event.pointerId);
      const around = host.selectedVertexIds.length === 1;
      const points = [snappedVectorPoint];
      const pendingAround = around && host.pendingKnifeAroundCut?.nodeId === host.primarySelectedNodeId && host.pendingKnifeAroundCut.vertexId === host.selectedVertexIds[0]
        ? host.pendingKnifeAroundCut.edgeIds
        : [];
      host.setKnifePreview({ points, edgeIds: pendingAround, mode: around ? "around" : "line" });
      host.dragRef.current = {
        mode: around ? "knifeAround" : "knifeLine",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        vertexId: around ? host.selectedVertexIds[0] : undefined,
        knifePoints: points,
        moved: false,
      };
      return;
    }

    const smartTransformBox = host.activeTool === "select" ? host.vectorSmartTransformBox : undefined;
    const smartTransformHit = smartTransformBox
      ? hitTestVectorSmartTransform(smartTransformBox, vectorPoint, host.cameraRef.current.zoom)
      : undefined;
    if (smartTransformBox && smartTransformHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const center = rectCenter(smartTransformBox.bounds);
      host.dragRef.current = {
        mode: smartTransformHit.type === "rotate" ? "vectorTransformRotate" : "vectorTransformResize",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map([[host.primarySelectedNodeId, smartTransformBox.bounds]]),
        selectionBounds: smartTransformBox.bounds,
        handle: smartTransformHit.type === "resize" ? smartTransformHit.handle : undefined,
        rotationHandle: smartTransformHit.type === "rotate" ? smartTransformHit.handle : undefined,
        rotationCenter: center,
        startRotationAngle: Math.atan2(vectorPoint.y - center.y, vectorPoint.x - center.x),
        nodeIds: [host.primarySelectedNodeId],
        vertexId: host.selectedVertexIds.at(-1),
        startVertices: smartTransformBox.vertices.map(vertex => ({ ...vertex })),
        startSmartTransformRotation: smartTransformBox.rotation,
        moved: false,
      };
      return;
    }

    const handleHit = hitTestVectorHandle(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
    if (handleHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      host.vectorActions.onSelectedHandleChange({ edgeId: handleHit.edgeId, endpoint: handleHit.endpoint });
      host.dragRef.current = {
        mode: "handle",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        edgeId: handleHit.edgeId,
        endpoint: handleHit.endpoint,
        startHandle: { x: handleHit.x, y: handleHit.y },
        moved: false,
      };
      return;
    }

    const vertexHit = hitTestVectorVertex(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
    if (host.activeTool === "path" && vertexHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      if (host.selectedVertexId && host.selectedVertexId !== vertexHit.id) {
        const connected = host.vectorActions.onPenConnectVertices(host.primarySelectedNodeId, host.selectedVertexId, vertexHit.id);
        const fromVertex = findVectorVertex(documentStore.getDocument(), host.primarySelectedNodeId, host.selectedVertexId);
        if (connected && fromVertex) {
          host.dragRef.current = {
            mode: "penCurve",
            startClientX: event.clientX,
            startClientY: event.clientY,
            startDocumentX: snappedVectorPoint.x,
            startDocumentY: snappedVectorPoint.y,
            startBoundsById: new Map(),
            nodeIds: [host.primarySelectedNodeId],
            edgeId: connected.edgeId,
            penCurveEdge: {
              edgeId: connected.edgeId,
              from: fromVertex,
              to: vertexHit,
              point: vectorPoint,
              t: 1,
            },
            moved: false,
          };
          return;
        }
      }

      host.vectorActions.onSelectedVertexChange(vertexHit.id);
      host.dragRef.current = {
        mode: "pathInsert",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        vertexId: vertexHit.id,
        moved: false,
      };
      return;
    }

    if (vertexHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === host.primarySelectedNodeId);
      let nextVertexIds = event.shiftKey
        ? toggleVertexSelection(host.selectedVertexIds, vertexHit.id)
        : host.selectedVertexIds.includes(vertexHit.id)
          ? host.selectedVertexIds
          : [vertexHit.id];
      let dragVertexId = vertexHit.id;
      let dragStartVertex = { x: vertexHit.x, y: vertexHit.y };
      let dragStartVertices = node?.type === "vectorNetwork"
        ? node.network.vertices
            .filter(vertex => nextVertexIds.includes(vertex.id))
            .map(vertex => ({ vertexId: vertex.id, x: vertex.x, y: vertex.y }))
        : [{ vertexId: vertexHit.id, x: vertexHit.x, y: vertexHit.y }];
      if (event.altKey && nextVertexIds.length > 0) {
        const duplicated = host.vectorActions.onDuplicateVerticesDragStart(host.primarySelectedNodeId, nextVertexIds, vertexHit.id);
        if (duplicated?.activeVertexId) {
          nextVertexIds = duplicated.vertexIds;
          dragVertexId = duplicated.activeVertexId;
          dragStartVertices = duplicated.vertices;
          const activeStart = duplicated.vertices.find(vertex => vertex.vertexId === dragVertexId);
          if (activeStart) {
            dragStartVertex = { x: activeStart.x, y: activeStart.y };
          }
        }
      } else if (event.shiftKey) {
        host.vectorActions.onSelectedVerticesChange(nextVertexIds);
      } else if (host.selectedVertexIds.includes(vertexHit.id)) {
        host.vectorActions.onSelectedVerticesChange(nextVertexIds);
      } else {
        host.vectorActions.onSelectedVertexChange(vertexHit.id);
      }
      host.dragRef.current = {
        mode: "vertex",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        vertexId: dragVertexId,
        startVertex: dragStartVertex,
        startVertices: dragStartVertices,
        moved: false,
      };
      return;
    }

    const edgeHit = hitTestVectorEdge(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
    if ((event.metaKey || event.ctrlKey) && edgeHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      host.vectorActions.onSelectedVertexChange(undefined);
      host.dragRef.current = {
        mode: "edgeBend",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        edgeId: edgeHit.edgeId,
        startEdge: edgeHit,
        moved: false,
      };
      return;
    }

    const intersectionHit = host.activeTool === "path" || host.activeTool === "select"
      ? hitTestVectorIntersection(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom)
      : undefined;
    if (intersectionHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      host.vectorActions.onPathInsertIntersection(host.primarySelectedNodeId, intersectionHit.point, intersectionHit.edges);
      host.dragRef.current = {
        mode: "pathInsert",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        edgeId: intersectionHit.edges[0].edgeId,
        moved: false,
      };
      return;
    }

    const insertion = edgeHit ? insertionForEdge(host.activeTool, edgeHit, vectorPoint, host.cameraRef.current.zoom) : undefined;
    if (edgeHit && insertion) {
      event.currentTarget.setPointerCapture(event.pointerId);
      if (host.activeTool === "path" && host.selectedVertexId) {
        host.vectorActions.onPenConnectToInsertedVertex(
          host.primarySelectedNodeId,
          host.selectedVertexId,
          edgeHit.edgeId,
          host.snapToPixelGrid ? snapPointToPixelGrid(insertion.point) : insertion.point,
          insertion.t,
        );
      } else {
        host.vectorActions.onPathInsertVertex(host.primarySelectedNodeId, edgeHit.edgeId, host.snapToPixelGrid ? snapPointToPixelGrid(insertion.point) : insertion.point, insertion.t);
      }
      host.dragRef.current = {
        mode: "pathInsert",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        edgeId: edgeHit.edgeId,
        moved: false,
      };
      return;
    }

    if (host.activeTool === "select" && edgeHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === host.primarySelectedNodeId);
      const nextEdgeIds = event.shiftKey
        ? toggleEdgeSelection(host.selectedEdgeIds, edgeHit.edgeId, true)
        : host.selectedEdgeIds.includes(edgeHit.edgeId)
          ? host.selectedEdgeIds
          : [edgeHit.edgeId];
      host.vectorActions.onSelectedEdgesChange(nextEdgeIds);
      const edgeVertices = node?.type === "vectorNetwork" ? verticesForEdges(node, nextEdgeIds) : [];
      host.dragRef.current = {
        mode: edgeVertices.length > 0 ? "edge" : "pathInsert",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        edgeIds: nextEdgeIds,
        edgeId: edgeHit.edgeId,
        startVertices: edgeVertices,
        moved: false,
      };
      return;
    }

    if (host.activeTool === "path") {
      event.currentTarget.setPointerCapture(event.pointerId);
      const fromVertex = host.selectedVertexId ? findVectorVertex(documentStore.getDocument(), host.primarySelectedNodeId, host.selectedVertexId) : undefined;
      const constrainedPoint = host.snapToPixelGrid
        ? snapPointToPixelGrid(event.shiftKey && fromVertex ? snapPointToAngle(fromVertex, snappedVectorPoint) : snappedVectorPoint)
        : event.shiftKey && fromVertex
          ? snapPointToAngle(fromVertex, vectorPoint)
          : vectorPoint;
      const added = host.vectorActions.onPenAddVertex(host.primarySelectedNodeId, host.selectedVertexId, constrainedPoint);
      host.dragRef.current = {
        mode: added?.edgeId && fromVertex ? "penCurve" : "pathInsert",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedVectorPoint.x,
        startDocumentY: snappedVectorPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.primarySelectedNodeId],
        vertexId: added?.vertexId,
        edgeId: added?.edgeId,
        penCurveEdge:
          added?.edgeId && fromVertex
            ? {
                edgeId: added.edgeId,
                from: fromVertex,
                to: { x: constrainedPoint.x, y: constrainedPoint.y },
                point: constrainedPoint,
                t: 1,
              }
            : undefined,
        moved: false,
      };
      return;
    }

    const nodeHit = hitTestDocument(documentStore.getDocument(), point, undefined, host.cameraRef.current.zoom, host.hitTestOptions({
      deepSelect: deepSelectModifier(event),
      selectedNodeIds: host.editableSelectedNodeIds,
    }));
    event.currentTarget.setPointerCapture(event.pointerId);
    host.dragRef.current = {
      mode: "vectorMarquee",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: new Map(),
      nodeIds: [host.primarySelectedNodeId],
      exitVectorModeOnClick: nodeHit.type !== "node" || nodeHit.nodeId !== host.primarySelectedNodeId,
      moved: false,
    };
    return;
  }

  if (host.mode === "select" && host.activeTool === "select") {
    const originHit = hitTestRotationOrigin(documentStore.getDocument(), host.visibleRotationOriginNodeId, host.editableSelectedNodeIds, point, host.cameraRef.current.zoom, host.previews);
    if (originHit) {
      event.currentTarget.setPointerCapture(event.pointerId);
      const snapPoints = rotationOriginSnapPoints(documentStore.getDocument(), originHit.node, host.previews);
      host.dragRef.current = {
        mode: "origin",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: point.x,
        startDocumentY: point.y,
        startBoundsById: new Map([[originHit.node.id, originHit.node.bounds]]),
        startTransformById: new Map([[originHit.node.id, originHit.node.transform]]),
        nodeIds: [originHit.node.id],
        startOrigin: originHit.origin,
        moved: false,
      };
      host.setOriginPreview({ nodeId: originHit.node.id, ...originHit.origin, snapPoints });
      host.setPointerHover({ hoveredRotationOrigin: false });
      return;
    }
  }

  // ── Primitive gizmo hit test ─────────────────────────────────────────────
  if (host.mode === "select" && host.activeTool === "select" && host.editableSelectedNodeIds.length === 1) {
    const primNodeId = host.editableSelectedNodeIds[0]!;
    const primNode = getAllSceneNodes(documentStore.getDocument()).find(n => n.id === primNodeId);
    if (primNode?.type === "primitive") {
      const primPoint = nodeSelectionPointToLocal(documentStore.getDocument(), primNodeId, point, host.previews);
      const primHit = hitTestPrimitiveGizmo(primNode, primPoint, host.cameraRef.current.zoom);
      if (primHit) {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        host.dragRef.current = {
          mode: "primitiveGizmo",
          startClientX: event.clientX,
          startClientY: event.clientY,
          startDocumentX: primPoint.x,
          startDocumentY: primPoint.y,
          startBoundsById: new Map([[primNodeId, primNode.bounds]]),
          nodeIds: [primNodeId],
          primitiveGizmoHandle: primHit,
          startPrimitiveSides: primNode.primitive.kind === "polygon" ? primNode.primitive.sides : undefined,
          startPrimitivePoints: primNode.primitive.kind === "star" ? primNode.primitive.points : undefined,
          moved: false,
        };
        return;
      }
    }
  }

  if (host.mode === "select" && host.activeTool === "select" && host.activeGradient) {
    const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), host.activeGradient.node.id, point, host.previews);
    const gradientHit = hitTestGradientGizmo(host.activeGradient.node.bounds, host.activeGradient.paint, localPoint, host.cameraRef.current.zoom);
    if (gradientHit) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      host.setActiveGradientHandle(gradientHandleKey(gradientHit));
      host.dragRef.current = {
        mode: "gradient",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.activeGradient.node.id],
        gradientHandle: gradientHit,
        gradientTarget: host.activeGradient.target,
        startGradientPaint: host.activeGradient.paint,
        moved: false,
      };
      return;
    }
  }

  if (host.mode === "select" && host.activeTool === "select" && host.activeImageFill) {
    const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), host.activeImageFill.node.id, point, host.previews);
    const imageHit = hitTestImageFillGizmo(host.activeImageFill.node.bounds, host.activeImageFill.paint, localPoint, host.cameraRef.current.zoom);
    if (imageHit) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      host.setActiveImageHandle(imageHit);
      host.dragRef.current = {
        mode: "imageFill",
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDocumentX: snappedPoint.x,
        startDocumentY: snappedPoint.y,
        startBoundsById: new Map(),
        nodeIds: [host.activeImageFill.node.id],
        imageHandle: imageHit,
        imageTarget: host.activeImageFill.target,
        startImagePaint: host.activeImageFill.paint,
        moved: false,
      };
      return;
    }
  }

  const selectionRotation = selectionRotationForNodes(documentStore.getDocument(), host.editableSelectedNodeIds);
  const selectionRotationOrigin = selectionRotationOriginForNodes(documentStore.getDocument(), host.editableSelectedNodeIds, host.selectionTransformBounds);
  const rotationHit = host.mode === "select" && host.activeTool === "select"
    ? hitTestSelectionRotationHandle(
      documentStore.getDocument(),
      host.editableSelectedNodeIds,
      host.selectionTransformBounds,
      point,
      host.cameraRef.current.zoom,
      selectionRotation,
      selectionRotationOrigin,
      host.previews,
    )
    : undefined;
  if (rotationHit) {
    event.currentTarget.setPointerCapture(event.pointerId);
    const activeNodeIds = host.editableSelectedNodeIds;
    const boundsById = new Map(
      getAllSceneNodes(documentStore.getDocument())
        .filter(node => activeNodeIds.includes(node.id))
        .map(node => [node.id, node.bounds] as const),
    );
    const transformById = new Map(
      getAllSceneNodes(documentStore.getDocument())
        .filter(node => activeNodeIds.includes(node.id))
        .map(node => [node.id, node.transform] as const),
    );
    const rotationCenter = host.selectionTransformBounds ? selectionRotationOrigin ?? rectCenter(host.selectionTransformBounds) : undefined;
    host.dragRef.current = {
      mode: "rotate",
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDocumentX: snappedPoint.x,
      startDocumentY: snappedPoint.y,
      startBoundsById: boundsById,
      startTransformById: transformById,
      selectionBounds: selectionBoundsForTransforms(documentStore.getDocument(), activeNodeIds, []),
      rotationHandle: rotationHit,
      rotationCenter,
      startRotationAngle: rotationCenter ? Math.atan2(point.y - rotationCenter.y, point.x - rotationCenter.x) : undefined,
      nodeIds: activeNodeIds,
      moved: false,
    };
    host.syncCanvasCursor();
    return;
  }

  const hit = timeDebugStep("hitTestDocument", "input", "pointerdown", () =>
    hitTestDocument(documentStore.getDocument(), point, host.selectionTransformBounds, host.cameraRef.current.zoom, host.hitTestOptions({
      deepSelect: deepSelectModifier(event),
      selectedNodeIds: host.editableSelectedNodeIds,
    })));
  const hitNode = hit.type === "node" ? getAllSceneNodes(documentStore.getDocument()).find(node => node.id === hit.nodeId) : undefined;
  event.currentTarget.setPointerCapture(event.pointerId);

  const nextSelection =
    hit.type === "node"
      ? applySelectionPointerDown(host.selectedNodeIds, hit.nodeId, event.shiftKey)
      : event.shiftKey
        ? host.selectedNodeIds
        : [];

  if (hit.type === "node") {
    host.setActiveGuideSelection(undefined);
    host.documentActions.onGuideSelectionChange(undefined);
    host.documentActions.onSelectionChange(nextSelection, nextSelection.includes(hit.nodeId) ? hit.nodeId : nextSelection.at(-1));
    host.syncOverlayRender(
      { selectedNodeIds: editableNodeIds(documentStore.getDocument(), nextSelection) },
      { immediate: true },
    );
  }
  if (!continuesDoubleClickSequence) {
    host.updateDoubleClickBaseFromSelection(nextSelection);
  }

  const activeNodeIds = hit.type === "node" ? nextSelection : host.editableSelectedNodeIds;
  const boundsById = new Map(
    getAllSceneNodes(documentStore.getDocument())
      .filter(node => activeNodeIds.includes(node.id))
      .map(node => [node.id, node.bounds] as const),
  );
  const transformById = new Map(
    getAllSceneNodes(documentStore.getDocument())
      .filter(node => activeNodeIds.includes(node.id))
      .map(node => [node.id, node.transform] as const),
  );
  const dragMode = hit.type === "resizeHandle" ? "resize" : hit.type === "node" ? "move" : "marquee";

  host.dragRef.current = {
    mode: dragMode,
    startClientX: event.clientX,
    startClientY: event.clientY,
    startDocumentX: snappedPoint.x,
    startDocumentY: snappedPoint.y,
    startBoundsById: boundsById,
    startTransformById: transformById,
    selectionBounds: selectionBoundsForTransforms(documentStore.getDocument(), activeNodeIds, []),
    handle: hit.type === "resizeHandle" ? hit.handle : undefined,
    nodeIds: activeNodeIds,
    objectSnapCandidates: dragMode === "move" ? visibleObjectSnapCandidates(documentStore.getDocument(), activeNodeIds, canvasViewportBounds(host.canvasRef.current, host.cameraRef.current)) : undefined,
    deselectOnClick: hit.type === "node" && hitNode?.type === "frame" && host.selectedNodeIds.includes(hit.nodeId),
    moved: false,
  };
  if (dragMode === "move") {
    host.moveAutopanPointerRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      pointerId: event.pointerId,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
    };
    host.startMoveAutopan();
  }
  host.syncCanvasCursor();
}

function clearPointerHover(): void {
  if (clearPointerHoverState(host.hoverStateRef.current)) {
    host.syncCanvasCursor();
    host.syncOverlayRender(undefined, { immediate: true });
  }
}

function handlePointerMoveImpl(event: React.PointerEvent<Element>): void {
  timeDebugStep("updatePointerStatus", "input", "", () => {
    host.updatePointerStatus(event.clientX, event.clientY);
  });

  if (host.colorSampler && host.canvasRef.current) {
    host.setColorSamplePreview(sampleRenderedCanvas(host.canvasRef.current, event.clientX, event.clientY));
    return;
  }

  if (host.canvasRef.current) {
    const rect = host.canvasRef.current.getBoundingClientRect();
    const insets = host.visibleViewportInsets ?? { top: 0, left: 0, right: 0, bottom: 0 };
    const canvasOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const insideCanvas = pointInsideCanvasContentArea(event.clientX, event.clientY, host.canvasRef.current, insets);
    host.setPointerCanvasOffset(canvasOffset);
    host.setPointerInsideCanvas(insideCanvas);
    if (insideCanvas) {
      host.setPointerDocumentPosition(screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current));
    }
  }

  const drag = host.dragRef.current;
  if (!drag || !host.canvasRef.current) {
    if (host.spacePanModifierRef.current) {
      clearPointerHover();
      return;
    }

    const frameLabelHit = timeDebugStep("frameLabelOverlays+hit", "input", "", () => {
      const labels = timeDebugStep("frameLabelOverlays", "input", "", () =>
        frameLabelOverlays(documentStore.getDocument(), host.cameraRef.current, host.previews, host.resolvedActivePageId, frameLabelViewportBounds(), {
          allowScripts: uiStore.allowScripts.getValue(),
        }));
      return timeDebugStep("hitTestFrameLabel", "input", "", () =>
        hitTestFrameLabel(labels, event.clientX, event.clientY));
    });
    if (frameLabelHit) {
      host.setPointerHover({
        hoveredFrameLabelId: frameLabelHit.id,
        hoveredFrameLabelScriptAction: hitTestFrameLabelScriptAction(frameLabelHit, event.clientX, event.clientY),
        hoveredNodeId: undefined,
        hoveredResizeHandle: undefined,
        hoveredRotationHandle: undefined,
        hoveredRotationOrigin: false,
        hoveredVertexId: undefined,
        hoveredEdgeId: undefined,
        hoveredVectorHandle: undefined,
        hoveredGradientHandle: undefined,
        hoveredImageHandle: undefined,
        hoveredPrototypeNodeId: undefined,
        pathPreviewPoint: undefined,
        pathPreviewSegment: undefined,
      });
      return;
    }

    if (host.canvasRef.current && host.animationModeActive && host.vectorTargetNodeId) {
      const targetNodeId = host.vectorTargetNodeId;
      const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
      const handleHit = hitTestVectorHandle(documentStore.getDocument(), targetNodeId, point, host.cameraRef.current.zoom);
      const vertexHit = handleHit ? undefined : hitTestVectorVertex(documentStore.getDocument(), targetNodeId, point, host.cameraRef.current.zoom);
      const edgeHit = (handleHit || vertexHit) ? undefined : hitTestVectorEdge(documentStore.getDocument(), targetNodeId, point, host.cameraRef.current.zoom);
      host.setPointerHover({
        hoveredFrameLabelId: undefined,
        hoveredNodeId: undefined,
        hoveredResizeHandle: undefined,
        hoveredRotationHandle: undefined,
        hoveredRotationOrigin: false,
        hoveredVectorHandle: handleHit ? { edgeId: handleHit.edgeId, endpoint: handleHit.endpoint } : undefined,
        hoveredVertexId: vertexHit?.id,
        hoveredEdgeId: edgeHit?.edgeId,
        hoveredGradientHandle: undefined,
        hoveredImageHandle: undefined,
        hoveredPrototypeNodeId: undefined,
        pathPreviewPoint: undefined,
        pathPreviewSegment: undefined,
      });
      return;
    }

    if (host.canvasRef.current && host.mode === "vectorEdit" && host.activeTool === "sculpt" && host.primarySelectedNodeId) {
      const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
      const vectorPoint = vectorEditLocalPoint(documentStore.getDocument(), host.primarySelectedNodeId, point);
      const brush = uiStore.sculptBrush.getValue();
      viewStore.setSculptBrushPreview({
        nodeId: host.primarySelectedNodeId,
        x: vectorPoint.x,
        y: vectorPoint.y,
        radius: brush.size / 2,
      });
      clearPointerHover();
      return;
    }

    if (host.canvasRef.current && host.mode === "vectorEdit" && host.activeTool === "shapeBuilder" && host.primarySelectedNodeId) {
      const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
      const vectorPoint = vectorEditLocalPoint(documentStore.getDocument(), host.primarySelectedNodeId, point);
      const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === host.primarySelectedNodeId);
      const region = node?.type === "vectorNetwork" ? hitTestShapeBuilderRegion(node.network, vectorPoint) : undefined;
      if (region?.id !== host.shapeBuilderHoverRegionId) {
        host.setShapeBuilderHoverRegionId(region?.id);
      }
      host.setPointerHover({
        hoveredFrameLabelId: undefined,
        hoveredNodeId: undefined,
        hoveredResizeHandle: undefined,
        hoveredRotationHandle: undefined,
        hoveredVertexId: undefined,
        hoveredEdgeId: undefined,
        hoveredVectorHandle: undefined,
        pathPreviewPoint: undefined,
        pathPreviewSegment: undefined,
      });
    } else if (host.canvasRef.current && host.mode === "vectorEdit" && (host.activeTool === "path" || host.activeTool === "select" || host.activeTool === "fill") && host.primarySelectedNodeId) {
      const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
      const vectorPoint = vectorEditLocalPoint(documentStore.getDocument(), host.primarySelectedNodeId, point);
      const smartTransformHit = host.activeTool === "select" && host.vectorSmartTransformBox
        ? hitTestVectorSmartTransform(host.vectorSmartTransformBox, vectorPoint, host.cameraRef.current.zoom)
        : undefined;
      if (smartTransformHit) {
        host.setPointerHover({
          hoveredFrameLabelId: undefined,
          hoveredResizeHandle: smartTransformHit.type === "resize" ? smartTransformHit.handle : undefined,
          hoveredRotationHandle: smartTransformHit.type === "rotate" ? smartTransformHit.handle : undefined,
          hoveredVectorHandle: undefined,
          hoveredVertexId: undefined,
          hoveredEdgeId: undefined,
          pathPreviewPoint: undefined,
          pathPreviewSegment: undefined,
          hoveredNodeId: undefined,
        });
        return;
      }
      const handleHit = hitTestVectorHandle(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
      const vertexHit = handleHit ? undefined : hitTestVectorVertex(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
      const edgeHit = hitTestVectorEdge(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom);
      const intersectionHit = !handleHit && !vertexHit && (host.activeTool === "path" || host.activeTool === "select")
        ? hitTestVectorIntersection(documentStore.getDocument(), host.primarySelectedNodeId, point, host.cameraRef.current.zoom)
        : undefined;
      const previewPoint = edgeHit ? previewPointForEdge(host.activeTool, edgeHit) : undefined;
      host.setPointerHover({
        hoveredFrameLabelId: undefined,
        pathPreviewPoint: intersectionHit
          ? { nodeId: host.primarySelectedNodeId, edgeId: intersectionHit.edges[0].edgeId, ...intersectionHit.point }
          : edgeHit && previewPoint
            ? { nodeId: host.primarySelectedNodeId, edgeId: edgeHit.edgeId, ...previewPoint }
            : undefined,
        pathPreviewSegment: pathPreviewSegmentForCursor(documentStore.getDocument(), host.primarySelectedNodeId, host.selectedVertexId, vectorPoint, host.activeTool, event.shiftKey, host.snapToPixelGrid),
        hoveredVectorHandle: host.activeTool === "fill" ? undefined : handleHit ? { edgeId: handleHit.edgeId, endpoint: handleHit.endpoint } : undefined,
        hoveredVertexId: host.activeTool === "fill" ? undefined : vertexHit?.id,
        hoveredEdgeId: host.activeTool === "fill" || handleHit || vertexHit ? undefined : edgeHit?.edgeId,
        hoveredResizeHandle: undefined,
        hoveredRotationHandle: undefined,
        hoveredNodeId: undefined,
      });
    } else if (host.canvasRef.current && host.mode === "select" && host.activeTool === "select") {
      const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
      if (host.prototypeModeActive) {
        const prototypeLinkHit = hitTestPrototypeLink(documentStore.getDocument(), host.prototypeLinksList, point, host.cameraRef.current.zoom);
        if (prototypeLinkHit) {
          host.setPointerHover({
            hoveredFrameLabelId: undefined,
            hoveredRotationOrigin: false,
            hoveredPrototypeNodeId: undefined,
            hoveredNodeId: undefined,
            hoveredResizeHandle: undefined,
            hoveredRotationHandle: undefined,
            hoveredVertexId: undefined,
            hoveredEdgeId: undefined,
            hoveredVectorHandle: undefined,
            hoveredGradientHandle: undefined,
            hoveredImageHandle: undefined,
            pathPreviewPoint: undefined,
            pathPreviewSegment: undefined,
          });
          return;
        }
      const hit = timeDebugStep("hitTestDocument", "input", "prototype+select", () => {
        const prototypeHit = hitTestPrototypeAnchor(documentStore.getDocument(), point, host.cameraRef.current.zoom, host.resolvedActivePageId);
        return prototypeHit
          ? { type: "node" as const, nodeId: prototypeHit.node.id }
          : hitTestDocument(documentStore.getDocument(), point, host.selectionTransformBounds, host.cameraRef.current.zoom, host.hitTestOptions({
              deepSelect: deepSelectModifier(event),
              selectedNodeIds: host.editableSelectedNodeIds,
            }));
      });
        host.setPointerHover({
          hoveredFrameLabelId: undefined,
          hoveredRotationOrigin: false,
          hoveredPrototypeNodeId: hit.type === "node" ? hit.nodeId : undefined,
          hoveredNodeId: hit.type === "node" ? hit.nodeId : undefined,
          hoveredResizeHandle: undefined,
          hoveredRotationHandle: undefined,
          hoveredVertexId: undefined,
          hoveredEdgeId: undefined,
          hoveredVectorHandle: undefined,
          hoveredGradientHandle: undefined,
          hoveredImageHandle: undefined,
          pathPreviewPoint: undefined,
          pathPreviewSegment: undefined,
        });
        return;
      }
      const originHit = hitTestRotationOrigin(documentStore.getDocument(), host.visibleRotationOriginNodeId, host.editableSelectedNodeIds, point, host.cameraRef.current.zoom, host.previews);
      if (originHit) {
        host.setPointerHover({
          hoveredFrameLabelId: undefined,
          hoveredRotationOrigin: true,
          hoveredNodeId: undefined,
          hoveredResizeHandle: undefined,
          hoveredRotationHandle: undefined,
          hoveredVertexId: undefined,
          hoveredEdgeId: undefined,
          hoveredVectorHandle: undefined,
          hoveredGradientHandle: undefined,
          hoveredImageHandle: undefined,
          hoveredPrototypeNodeId: undefined,
          pathPreviewSegment: undefined,
        });
        return;
      }

      // Primitive gizmo hover
      if (host.editableSelectedNodeIds.length === 1) {
        const primNodeId = host.editableSelectedNodeIds[0]!;
        const primNode = getAllSceneNodes(documentStore.getDocument()).find(n => n.id === primNodeId);
        if (primNode?.type === "primitive") {
          const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), primNodeId, point, host.previews);
          const primHit = hitTestPrimitiveGizmo(primNode, localPoint, host.cameraRef.current.zoom);
          if (primHit) {
            host.setPointerHover({
              hoveredFrameLabelId: undefined,
              hoveredRotationOrigin: false,
              hoveredPrimitiveGizmoHandle: primHit,
              hoveredNodeId: undefined,
              hoveredResizeHandle: undefined,
              hoveredVertexId: undefined,
              hoveredEdgeId: undefined,
              hoveredVectorHandle: undefined,
              hoveredGradientHandle: undefined,
              hoveredImageHandle: undefined,
              pathPreviewSegment: undefined,
              hoveredPrototypeNodeId: undefined,
            });
            return;
          }
        }
      }

      if (host.activeGradient) {
        const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), host.activeGradient.node.id, point, host.previews);
        const gradientHit = hitTestGradientGizmo(host.activeGradient.node.bounds, host.activeGradient.paint, localPoint, host.cameraRef.current.zoom);
        if (gradientHit) {
          host.setPointerHover({
            hoveredFrameLabelId: undefined,
            hoveredRotationOrigin: false,
            hoveredGradientHandle: gradientHandleKey(gradientHit),
            hoveredNodeId: undefined,
            hoveredResizeHandle: undefined,
            hoveredVertexId: undefined,
            hoveredEdgeId: undefined,
            hoveredVectorHandle: undefined,
            pathPreviewSegment: undefined,
            hoveredPrototypeNodeId: undefined,
          });
          return;
        }
      }

      if (host.activeImageFill) {
        const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), host.activeImageFill.node.id, point, host.previews);
        const imageHit = hitTestImageFillGizmo(host.activeImageFill.node.bounds, host.activeImageFill.paint, localPoint, host.cameraRef.current.zoom);
        if (imageHit) {
          host.setPointerHover({
            hoveredFrameLabelId: undefined,
            hoveredRotationOrigin: false,
            hoveredImageHandle: imageHit,
            hoveredNodeId: undefined,
            hoveredResizeHandle: undefined,
            hoveredVertexId: undefined,
            hoveredEdgeId: undefined,
            hoveredVectorHandle: undefined,
            pathPreviewSegment: undefined,
            hoveredPrototypeNodeId: undefined,
          });
          return;
        }
      }

      const hit = timeDebugStep("hitTestDocument", "input", "select-hover", () =>
        hitTestDocument(documentStore.getDocument(), point, host.selectionTransformBounds, host.cameraRef.current.zoom, host.hitTestOptions({
          deepSelect: deepSelectModifier(event),
          selectedNodeIds: host.editableSelectedNodeIds,
        })));
      const rotationHit = timeDebugStep("hitTestRotationHandle", "input", "", () =>
        hitTestSelectionRotationHandle(
          documentStore.getDocument(),
          host.editableSelectedNodeIds,
          host.selectionTransformBounds,
          point,
          host.cameraRef.current.zoom,
          selectionRotationForNodes(documentStore.getDocument(), host.editableSelectedNodeIds),
          selectionRotationOriginForNodes(documentStore.getDocument(), host.editableSelectedNodeIds, host.selectionTransformBounds),
          host.previews,
        ));
      host.setPointerHover({
        hoveredFrameLabelId: undefined,
        hoveredRotationOrigin: false,
        hoveredNodeId: hit.type === "node" ? hit.nodeId : undefined,
        hoveredResizeHandle: rotationHit ? undefined : hit.type === "resizeHandle" ? hit.handle : undefined,
        hoveredRotationHandle: rotationHit,
        hoveredVertexId: undefined,
        hoveredEdgeId: undefined,
        hoveredVectorHandle: undefined,
        hoveredGradientHandle: undefined,
        hoveredImageHandle: undefined,
        hoveredPrimitiveGizmoHandle: undefined,
        hoveredPrototypeNodeId: undefined,
        pathPreviewSegment: undefined,
      });
      host.updateMeasureModifierState(host.devModeActive || event.altKey);
      if ((host.devModeActive || event.altKey) && host.editableSelectedNodeIds.length > 0) {
        host.scheduleOverlayRender();
      }
    } else {
      host.setPointerHover({
        hoveredFrameLabelId: undefined,
        hoveredNodeId: undefined,
        hoveredResizeHandle: undefined,
        hoveredRotationHandle: undefined,
        hoveredRotationOrigin: false,
        hoveredVertexId: undefined,
        hoveredEdgeId: undefined,
        hoveredVectorHandle: undefined,
        hoveredGradientHandle: undefined,
        hoveredImageHandle: undefined,
        hoveredPrototypeNodeId: undefined,
        pathPreviewPoint: undefined,
        pathPreviewSegment: undefined,
      });
    }
    return;
  }

  if (drag.mode === "move") {
    host.moveAutopanPointerRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      pointerId: event.pointerId,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
    };
  }

  const screenDx = event.clientX - drag.startClientX;
  const screenDy = event.clientY - drag.startClientY;

  if (drag.mode === "pan") {
    if (Math.hypot(screenDx, screenDy) >= 3) {
      drag.moved = true;
    }
    const zoom = host.cameraRef.current.zoom;
    host.updateCamera({
      ...host.cameraRef.current,
      x: drag.startDocumentX - screenDx / zoom,
      y: drag.startDocumentY - screenDy / zoom,
    });
    host.syncCanvasCursor();
    return;
  }

  if (Math.hypot(screenDx, screenDy) < 3 && !drag.moved) {
    return;
  }

  drag.moved = true;

  const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
  const editPoint = host.snapToPixelGrid ? snapPointToPixelGrid(point) : point;
  const dx = editPoint.x - drag.startDocumentX;
  const dy = editPoint.y - drag.startDocumentY;
  drag.lastDx = dx;
  drag.lastDy = dy;

  if ((drag.mode === "marquee" || drag.mode === "animationObjectLink") && drag.animationSourceNodeId) {
    const source = findSceneNode(documentStore.getDocument(), drag.animationSourceNodeId);
    const nextMode = animationFrameDragModeForPoint(source, point);
    if (nextMode) {
      drag.mode = nextMode;
      if (nextMode === "marquee") {
        host.setPrototypeLinkPreview(undefined);
        host.setAnimObjectDragGhost(undefined);
        const rect = normalizeRect({
          x: drag.startDocumentX,
          y: drag.startDocumentY,
          width: point.x - drag.startDocumentX,
          height: point.y - drag.startDocumentY,
        });
        const nextPreviews = [{ nodeId: "__marquee__", bounds: rect }];
        viewStore.setPreviews(nextPreviews);
        host.syncOverlayRender(undefined, { immediate: true });
      } else {
        viewStore.setPreviews([]);
        const acceptedEndpoint = host.animationGraph ? nearestAnimationObjectInputPin(host.animationGraph.nodes, point) : undefined;
        host.setPrototypeLinkPreview({ from: nodeCenter(source!), to: point });
        host.setAnimObjectDragGhost(
          acceptedEndpoint
            ? undefined
            : { point, kind: "animation" },
        );
        host.syncOverlayRender(undefined, { immediate: true });
      }
      return;
    }
  }

  if (drag.mode === "lasso") {
    const lastPoint = drag.lassoPoints?.at(-1);
    if (!lastPoint || Math.hypot(editPoint.x - lastPoint.x, editPoint.y - lastPoint.y) >= 1 / host.cameraRef.current.zoom) {
      drag.lassoPoints = [...(drag.lassoPoints ?? []), editPoint];
    }
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if ((drag.mode === "guideCreate" || drag.mode === "guideMove") && drag.guideOrientation) {
    const documentPosition = guideDocumentPositionFromPointer(drag.guideOrientation, point);
    host.setGuidePreview({
      orientation: drag.guideOrientation,
      documentPosition,
      selected: drag.mode === "guideMove",
    });
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag.mode === "shapeBuilder") {
    const nodeId = drag.nodeIds[0];
    const localPoint = nodeId ? vectorEditLocalPoint(documentStore.getDocument(), nodeId, point) : point;
    const node = nodeId ? getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId) : undefined;
    const region = node?.type === "vectorNetwork" ? hitTestShapeBuilderRegion(node.network, localPoint) : undefined;
    host.setShapeBuilderHoverRegionId(region?.id);
    if (region && drag.shapeBuilderMode) {
      host.applyShapeBuilderRegionSelection(region.id, drag.shapeBuilderMode);
    }
    return;
  }

  if (drag.mode === "pathInsert") {
    return;
  }

  if (drag.mode === "animationObjectLink" && drag.animationSourceNodeId) {
    const source = findSceneNode(documentStore.getDocument(), drag.animationSourceNodeId);
    if (!source) {
        cancelAnimationObjectLinkDrag();
      return;
    }
    const acceptedEndpoint = host.animationGraph ? nearestAnimationObjectInputPin(host.animationGraph.nodes, point) : undefined;
    const isVectorTargeting = host.vectorTargetNodeId && drag.animationSourceNodeId === host.vectorTargetNodeId;
    const vectorTargetCount = isVectorTargeting
      ? animationTargetsFromVectorSelection(host.vectorTargetNodeId!, selectionStore.getSelection()).length
      : 0;
    const fromPoint = nodeCenter(source);
    host.setPrototypeLinkPreview({ from: fromPoint, to: point });
    host.setAnimObjectDragGhost(
      acceptedEndpoint
        ? undefined
        : isVectorTargeting
          ? { point, kind: vectorTargetCount > 1 ? "group" : "animate" }
          : { point, kind: host.selectedNodeIds.length > 1 ? "group" : source.type === "frame" ? "animation" : "animate" },
    );
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag.mode === "prototypeLink" && drag.prototypeSourceNodeId) {
    const source = findSceneNode(documentStore.getDocument(), drag.prototypeSourceNodeId);
    if (!source) {
      return;
    }
    host.setPrototypeLinkPreview({ from: nodeCenter(source), to: point });
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag.mode === "textSelect" && host.selectedTextNode && drag.startTextIndex !== undefined) {
    const index = textIndexAtPoint(host.selectedTextNode, point, Math.round(host.cameraRef.current.zoom * 10));
    const next = { anchor: drag.startTextIndex, focus: index };
    host.updateTextSelection(next);
    setTextareaSelection(host.textInputRef.current, next.anchor, next.focus);
    return;
  }

  if (drag.mode === "gradient" && drag.gradientHandle && drag.gradientTarget && drag.startGradientPaint && drag.nodeIds[0]) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    if (!node) {
      return;
    }
    const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), node.id, point);

    if (drag.gradientHandle.type === "start" || drag.gradientHandle.type === "end") {
      if (drag.startGradientPaint.type === "linearGradient") {
        host.documentActions.onGradientPaintChange(node.id, drag.gradientTarget, paintWithGradientEndpoint(drag.startGradientPaint, drag.gradientHandle.type, node.bounds, localPoint));
      }
      return;
    }

    if (drag.gradientHandle.type === "center") {
      if (drag.startGradientPaint.type === "radialGradient") {
        host.documentActions.onGradientPaintChange(node.id, drag.gradientTarget, paintWithRadialCenter(drag.startGradientPaint, node.bounds, localPoint));
      }
      return;
    }

    if (drag.gradientHandle.type === "radius") {
      if (drag.startGradientPaint.type === "radialGradient") {
        host.documentActions.onGradientPaintChange(node.id, drag.gradientTarget, paintWithRadialRadiusPoint(drag.startGradientPaint, node.bounds, localPoint));
      }
      return;
    }

    const position = stopPositionForPoint(node.bounds, drag.startGradientPaint, localPoint);
    host.documentActions.onGradientPaintChange(
      node.id,
      drag.gradientTarget,
      drag.startGradientPaint.type === "radialGradient"
        ? paintWithRadialStopPosition(drag.startGradientPaint, drag.gradientHandle.stopId, position)
        : paintWithGradientStopPosition(drag.startGradientPaint, drag.gradientHandle.stopId, position),
    );
    return;
  }

  if (drag.mode === "primitiveGizmo" && drag.primitiveGizmoHandle && drag.nodeIds[0]) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    if (!node || node.type !== "primitive") return;
    const startBounds = drag.startBoundsById.get(node.id) ?? node.bounds;
    const localPoint = nodeSelectionPointToLocal(documentStore.getDocument(), node.id, point, host.previews);
    const startLocalPoint = { x: drag.startDocumentX, y: drag.startDocumentY };
    const handle = drag.primitiveGizmoHandle;
    let nextPrimitive = node.primitive;

    if (handle.type === "oval:arcEnd" && node.primitive.kind === "oval") {
      nextPrimitive = ovalWithArcEndDrag(node.primitive, startBounds, localPoint);
    } else if (handle.type === "oval:arcStart" && node.primitive.kind === "oval") {
      nextPrimitive = ovalWithArcStartDrag(node.primitive, startBounds, localPoint);
    } else if (handle.type === "oval:ratio" && node.primitive.kind === "oval") {
      nextPrimitive = ovalWithRatioDrag(node.primitive, startBounds, localPoint);
    } else if (handle.type === "polygon:count" && node.primitive.kind === "polygon") {
      nextPrimitive = polygonWithCountDrag(node.primitive, startBounds, localPoint, startLocalPoint, drag.startPrimitiveSides ?? node.primitive.sides);
    } else if (handle.type === "polygon:curvature" && node.primitive.kind === "polygon") {
      nextPrimitive = polygonWithCurvatureDrag(node.primitive, startBounds, localPoint);
    } else if (handle.type === "star:count" && node.primitive.kind === "star") {
      nextPrimitive = starWithCountDrag(node.primitive, startBounds, localPoint, startLocalPoint, drag.startPrimitivePoints ?? node.primitive.points);
    } else if (handle.type === "star:ratio" && node.primitive.kind === "star") {
      nextPrimitive = starWithRatioDrag(node.primitive, startBounds, localPoint);
    }

    host.documentActions.onPrimitiveChange(node.id, nextPrimitive);
    return;
  }

  if (drag.mode === "imageFill" && drag.imageHandle && drag.imageTarget && drag.startImagePaint && drag.nodeIds[0]) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    if (!node) {
      return;
    }

    const startLocalPoint = nodeSelectionPointToLocal(documentStore.getDocument(), node.id, { x: drag.startDocumentX, y: drag.startDocumentY });
    const currentLocalPoint = nodeSelectionPointToLocal(documentStore.getDocument(), node.id, point);
    const localDx = currentLocalPoint.x - startLocalPoint.x;
    const localDy = currentLocalPoint.y - startLocalPoint.y;
    const nextPaint = drag.imageHandle === "body"
      ? imagePaintWithMovedCrop(drag.startImagePaint, node.bounds, localDx, localDy)
      : imagePaintWithResizedCrop(drag.startImagePaint, node.bounds, drag.imageHandle, localDx, localDy);
    host.documentActions.onGradientPaintChange(node.id, drag.imageTarget, nextPaint);
    return;
  }

  if (drag.mode === "sculpt" && drag.nodeIds[0] && drag.sculptRadius && drag.sculptStrength !== undefined && drag.sculptPositions) {
    const sculptDocument = documentStore.getDocument();
    const nodeId = drag.nodeIds[0];
    const localPoint = vectorEditLocalPoint(sculptDocument, nodeId, point);
    const previousSample = drag.sculptLastSample ?? localPoint;
    applySculptSegment(drag.sculptPositions, previousSample, localPoint, {
      radius: drag.sculptRadius,
      strength: drag.sculptStrength,
    });
    drag.sculptLastSample = localPoint;
    drag.moved = true;
    const active = drag.sculptPositions[0];
    if (active) {
      viewStore.setVertexPreview({ nodeId, ...active, vertices: drag.sculptPositions.map(vertex => ({ ...vertex })) });
    }
    viewStore.setSculptBrushPreview({
      nodeId,
      x: localPoint.x,
      y: localPoint.y,
      radius: drag.sculptRadius,
    });
    return;
  }

  if (drag.mode === "penCurve" && drag.edgeId && drag.penCurveEdge && drag.nodeIds[0]) {
    const penDocument = documentStore.getDocument();
    const localPoint = vectorEditLocalPoint(penDocument, drag.nodeIds[0], point);
    const dragPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    const penNode = getAllSceneNodes(penDocument).find(candidate => candidate.id === drag.nodeIds[0]);
    const penNetwork = penNode?.type === "vectorNetwork" ? penNode.network : undefined;
    const fromVertexId = penNetwork?.edges.find(candidate => candidate.id === drag.edgeId)?.from.vertexId;
    const fromIncomingHandle = penNetwork && fromVertexId
      ? handlesAtVertex(penNetwork, fromVertexId).find(handle => handle.edgeId !== drag.edgeId)
      : undefined;
    const handles = penDragHandles(drag.penCurveEdge, dragPoint, fromIncomingHandle);
    viewStore.setEdgeBendPreview({
      nodeId: drag.nodeIds[0],
      edgeId: drag.edgeId,
      fromHandle: host.snapToPixelGrid ? snapPointToPixelGrid(handles.fromHandle) : handles.fromHandle,
      toHandle: host.snapToPixelGrid ? snapPointToPixelGrid(handles.toHandle) : handles.toHandle,
    });
    return;
  }

  if (drag.mode === "pencil") {
    const points = drag.pencilPoints ?? [];
    const rawPoints = drag.pencilRawPoints ?? [];
    const pencilPoint = pencilPointForTrace(point, drag.pencilTrace, host.snapToPixelGrid, host.cameraRef.current.zoom);
    const previous = points.at(-1);
    if (!previous || Math.hypot(pencilPoint.x - previous.x, pencilPoint.y - previous.y) >= 1.5 / host.cameraRef.current.zoom) {
      points.push(pencilPoint);
      rawPoints.push(point);
      drag.pencilPoints = points;
      drag.pencilRawPoints = rawPoints;
      host.scheduleDraftPencilRender(points);
    }
    return;
  }

  if ((drag.mode === "knifeLine" || drag.mode === "knifeAround") && drag.nodeIds[0]) {
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const localEditPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    const points = drag.mode === "knifeLine"
      ? [{ x: drag.startDocumentX, y: drag.startDocumentY }, localEditPoint]
      : [...(drag.knifePoints ?? []), localEditPoint];
    drag.knifePoints = points;
    const edgeIds = drag.mode === "knifeLine"
      ? knifeLineCuts(documentStore.getDocument(), drag.nodeIds[0], points[0]!, points[1]!).map(cut => cut.edgeId)
      : mergeSelection(
          host.pendingKnifeAroundCutRef.current?.nodeId === drag.nodeIds[0] && host.pendingKnifeAroundCutRef.current.vertexId === drag.vertexId
            ? host.pendingKnifeAroundCutRef.current.edgeIds
            : [],
          knifeAroundEdgeIds(documentStore.getDocument(), drag.nodeIds[0], drag.vertexId, points),
        );
    host.setKnifePreview({ points, edgeIds, mode: drag.mode === "knifeLine" ? "line" : "around" });
    return;
  }

  if (drag.mode === "move") {
    const flexReorder = timeDebugStep("drag:flexReorder", "input", "", () =>
      event.altKey ? undefined : flexReorderPreviewForDrag(documentStore.getDocument(), drag, dx, dy));
    drag.flexReorderTarget = flexReorder?.target;
    const snap = timeDebugStep("drag:movePreviews+snap", "input", "", () =>
      flexReorder ?? movePreviewsForDrag(
        drag,
        dx,
        dy,
        event.altKey,
        host.snapToPixelGrid,
        host.snapToObjects,
        host.snapToGuides,
        host.cameraRef.current.zoom,
        documentStore.getDocument(),
        host.resolvedActivePageId,
        host.editableSelectedNodeIds,
        event.shiftKey,
      ));
    timeDebugStep("drag:publishPreviews", "input", `${snap.previews.length} previews`, () => {
      viewStore.setPreviews(snap.previews);
      host.setObjectSnapGuides(snap.guides);
    });
    timeDebugStep("drag:buildOverlay", "input", `${snap.guides.length} guides`, () => {
      host.syncOverlayRender({ objectSnapGuides: snap.guides });
    });
    return;
  }

  if (drag.mode === "origin" && drag.nodeIds[0] && drag.startOrigin) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    if (!node) {
      return;
    }
    const snapPoints = rotationOriginSnapPoints(documentStore.getDocument(), node);
    const localDelta = nodeSelectionDragDeltaToLocal(documentStore.getDocument(), node.id, { x: dx, y: dy });
    const rawOrigin = { x: drag.startOrigin.x + localDelta.x, y: drag.startOrigin.y + localDelta.y };
    const snappedOrigin = snapRotationOrigin(rawOrigin, snapPoints, host.cameraRef.current.zoom);
    host.setOriginPreview({ nodeId: node.id, ...snappedOrigin, snapPoints });
    return;
  }

  if (drag.mode === "rotate" && drag.rotationCenter && drag.startRotationAngle !== undefined) {
    const angle = Math.atan2(point.y - drag.rotationCenter.y, point.x - drag.rotationCenter.x);
    const deltaDegrees = normalizeDegrees(((angle - drag.startRotationAngle) * 180) / Math.PI);
    viewStore.setPreviews(
      drag.nodeIds.flatMap(nodeId => {
        const bounds = drag.startBoundsById.get(nodeId);
        const transform = drag.startTransformById?.get(nodeId);
        return bounds && transform
          ? [{ nodeId, bounds, rotation: normalizeDegrees(transform.rotation + deltaDegrees) }]
          : [];
      }),
    );
    return;
  }

  if (drag.mode === "vectorTransformRotate" && drag.nodeIds[0] && drag.rotationCenter && drag.startRotationAngle !== undefined && drag.startVertices?.length) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const angle = Math.atan2(localPoint.y - drag.rotationCenter.y, localPoint.x - drag.rotationCenter.x);
    const freeDeltaDegrees = normalizeDegrees(((angle - drag.startRotationAngle) * 180) / Math.PI);
    const deltaDegrees = event.shiftKey ? snapDegrees(freeDeltaDegrees, 15) : freeDeltaDegrees;
    const rotation = normalizeDegrees((drag.startSmartTransformRotation ?? 0) + deltaDegrees);
    const positions = drag.startVertices.map(vertex => ({
      vertexId: vertex.vertexId,
      ...rotatePoint(vertex, drag.rotationCenter!, deltaDegrees),
    }));
    const handlePositions = node?.type === "vectorNetwork"
      ? rotateHandlesForVertices(node, drag.startVertices.map(vertex => vertex.vertexId), drag.rotationCenter, deltaDegrees)
      : undefined;
    const activePosition = positions.find(position => position.vertexId === drag.vertexId) ?? positions.at(-1);
    if (activePosition) {
      host.setVectorSmartTransform(host.vectorSmartTransformKey ? { key: host.vectorSmartTransformKey, rotation } : undefined);
      viewStore.setVertexPreview({
        nodeId: drag.nodeIds[0],
        vertexId: activePosition.vertexId,
        x: activePosition.x,
        y: activePosition.y,
        vertices: positions,
        handles: handlePositions,
      });
    }
    return;
  }

  if (drag.mode === "textDraw") {
    const draftShape: Parameters<typeof host.setDraftShape>[0] = {
      kind: "rectangle",
      bounds: shapeDrawBounds(
        { x: drag.startDocumentX, y: drag.startDocumentY },
        editPoint,
        "rectangle",
        false,
        false,
        host.snapToPixelGrid,
        "whole",
      ),
    };
    host.setDraftShape(draftShape);
    host.setObjectSnapGuides([]);
    host.syncOverlayRender({ draftShape, objectSnapGuides: [] }, { immediate: true });
    return;
  }

  if (drag.mode === "draw" && drag.shapeKind) {
    const drawn = shapeDrawBoundsForDrag(drag, editPoint, event.shiftKey, event.altKey);
    const draftShape: Parameters<typeof host.setDraftShape>[0] = {
      kind: drag.shapeKind,
      bounds: drawn.bounds,
    };
    host.setDraftShape(draftShape);
    host.setObjectSnapGuides(drawn.guides);
    host.syncOverlayRender({ draftShape, objectSnapGuides: drawn.guides }, { immediate: true });
    return;
  }

  if (drag.mode === "vectorTransformResize" && drag.nodeIds[0] && drag.selectionBounds && drag.handle && drag.startVertices?.length) {
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const rotation = drag.startSmartTransformRotation ?? 0;
    const startCenter = rectCenter(drag.selectionBounds);
    const startPointInBoxSpace = inverseRotatePoint({ x: drag.startDocumentX, y: drag.startDocumentY }, startCenter, rotation);
    const currentPointInBoxSpace = inverseRotatePoint(localPoint, startCenter, rotation);
    const nextBounds = resizeBounds(
      drag.selectionBounds,
      drag.handle,
      currentPointInBoxSpace.x - startPointInBoxSpace.x,
      currentPointInBoxSpace.y - startPointInBoxSpace.y,
    );
    const constrainedBounds = event.shiftKey
      ? constrainResizeBoundsAspectRatio(drag.selectionBounds, nextBounds, drag.handle)
      : nextBounds;
    const positions = drag.startVertices.map(vertex => ({
      vertexId: vertex.vertexId,
      ...transformPointBetweenRotatedBounds(vertex, drag.selectionBounds!, constrainedBounds, rotation),
    }));
    const activePosition = positions.find(position => position.vertexId === drag.vertexId) ?? positions.at(-1);
    if (activePosition) {
      viewStore.setVertexPreview({
        nodeId: drag.nodeIds[0],
        vertexId: activePosition.vertexId,
        x: activePosition.x,
        y: activePosition.y,
        vertices: positions,
      });
    }
    return;
  }

  if (drag.mode === "vertex" && drag.vertexId && drag.startVertex && drag.nodeIds[0]) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const localEditPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    const localDx = localEditPoint.x - drag.startDocumentX;
    const localDy = localEditPoint.y - drag.startDocumentY;
    const activeStartVertex = drag.startVertices?.find(vertex => vertex.vertexId === drag.vertexId) ?? { vertexId: drag.vertexId, ...drag.startVertex };
    let x = host.snapToPixelGrid ? snapToPixelGridValue(activeStartVertex.x + localDx) : activeStartVertex.x + localDx;
    let y = host.snapToPixelGrid ? snapToPixelGridValue(activeStartVertex.y + localDy) : activeStartVertex.y + localDy;
    if (node?.type === "vectorNetwork") {
      const geometrySnap = host.snapToGeometry
        ? snapVectorPointToGeometry(node.network.vertices, drag.vertexId, { x, y }, host.cameraRef.current.zoom)
        : undefined;
      if (geometrySnap) {
        x = geometrySnap.point.x;
        y = geometrySnap.point.y;
        host.setObjectSnapGuides(vectorEditGuidesToWorld(documentStore.getDocument(), drag.nodeIds[0], geometrySnap.guides));
      } else {
        host.setObjectSnapGuides([]);
      }
      ({ x, y } = snapPointToVertex({ x, y }, node.network.vertices, drag.vertexId));
    }
    const finalDx = x - activeStartVertex.x;
    const finalDy = y - activeStartVertex.y;
    const vertexPreviews = (drag.startVertices?.length ? drag.startVertices : [activeStartVertex]).map(vertex => ({
      vertexId: vertex.vertexId,
      x: host.snapToPixelGrid ? snapToPixelGridValue(vertex.x + finalDx) : vertex.x + finalDx,
      y: host.snapToPixelGrid ? snapToPixelGridValue(vertex.y + finalDy) : vertex.y + finalDy,
    }));

    viewStore.setVertexPreview({
      nodeId: drag.nodeIds[0],
      vertexId: drag.vertexId,
      x,
      y,
      vertices: vertexPreviews,
    });
    return;
  }

  if (drag.mode === "edge" && drag.nodeIds[0] && drag.startVertices?.length) {
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const localEditPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    const localDx = localEditPoint.x - drag.startDocumentX;
    const localDy = localEditPoint.y - drag.startDocumentY;
    const vertexPreviews = drag.startVertices.map(vertex => ({
      vertexId: vertex.vertexId,
      x: host.snapToPixelGrid ? snapToPixelGridValue(vertex.x + localDx) : vertex.x + localDx,
      y: host.snapToPixelGrid ? snapToPixelGridValue(vertex.y + localDy) : vertex.y + localDy,
    }));
    const activePosition = vertexPreviews.at(-1);
    if (activePosition) {
      viewStore.setVertexPreview({
        nodeId: drag.nodeIds[0],
        vertexId: activePosition.vertexId,
        x: activePosition.x,
        y: activePosition.y,
        vertices: vertexPreviews,
      });
    }
    return;
  }

  if (drag.mode === "extrude" && drag.nodeIds[0] && drag.edgeIds?.length) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    if (node?.type !== "vectorNetwork") {
      return;
    }
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const localEditPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    let localDx = localEditPoint.x - drag.startDocumentX;
    let localDy = localEditPoint.y - drag.startDocumentY;
    if (event.shiftKey) {
      const constrained = constrainExtrudeDragDelta(localDx, localDy, node.network, drag.edgeIds);
      localDx = constrained.dx;
      localDy = constrained.dy;
    }
    drag.lastDx = localDx;
    drag.lastDy = localDy;
    const cmdHeld = event.metaKey || event.ctrlKey;
    const result = extrudeEdges(node.network, drag.edgeIds, {
      dx: localDx,
      dy: localDy,
      mutual: event.altKey && drag.edgeIds.length >= 2,
      relative: cmdHeld,
      primaryEdgeId: drag.edgeId,
    });
    if (host.snapToPixelGrid) {
      const newIds = new Set(result.newVertexIds);
      for (const vertex of result.network.vertices) {
        if (!newIds.has(vertex.id)) {
          continue;
        }
        vertex.x = snapToPixelGridValue(vertex.x);
        vertex.y = snapToPixelGridValue(vertex.y);
      }
    }
    viewStore.setVectorTopologyPreview({
      nodeId: node.id,
      network: result.network,
      changedEdgeIds: [...result.newEdgeIds, ...result.connectorEdgeIds],
      changedVertexIds: result.newVertexIds,
      newVertexIds: result.newVertexIds,
      newEdgeIds: result.newEdgeIds,
    });
    return;
  }

  if (drag.mode === "handle" && drag.edgeId && drag.endpoint && drag.startHandle && drag.nodeIds[0]) {
    const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === drag.nodeIds[0]);
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const localEditPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    const localDx = localEditPoint.x - drag.startDocumentX;
    const localDy = localEditPoint.y - drag.startDocumentY;
    const position = {
      x: host.snapToPixelGrid ? snapToPixelGridValue(drag.startHandle.x + localDx) : drag.startHandle.x + localDx,
      y: host.snapToPixelGrid ? snapToPixelGridValue(drag.startHandle.y + localDy) : drag.startHandle.y + localDy,
    };
    const updates = node?.type === "vectorNetwork"
      ? vectorHandleUpdatesForMove(node.network, { edgeId: drag.edgeId, endpoint: drag.endpoint }, position)
      : [{ edgeId: drag.edgeId, endpoint: drag.endpoint, ...position }];
    const mirroredHandle = updates.find(update => update.edgeId !== drag.edgeId || update.endpoint !== drag.endpoint);
    viewStore.setHandlePreview({
      nodeId: drag.nodeIds[0],
      edgeId: drag.edgeId,
      endpoint: drag.endpoint,
      ...position,
      mirroredHandle,
    });
    return;
  }

  if (drag.mode === "edgeBend" && drag.edgeId && drag.startEdge && drag.nodeIds[0]) {
    const localPoint = vectorEditLocalPoint(documentStore.getDocument(), drag.nodeIds[0], point);
    const localEditPoint = host.snapToPixelGrid ? snapPointToPixelGrid(localPoint) : localPoint;
    const handles = bentEdgeHandles(drag.startEdge, localEditPoint.x - drag.startDocumentX, localEditPoint.y - drag.startDocumentY);
    const nextPreview = {
      nodeId: drag.nodeIds[0],
      edgeId: drag.edgeId,
      fromHandle: host.snapToPixelGrid ? snapPointToPixelGrid(handles.fromHandle) : handles.fromHandle,
      toHandle: host.snapToPixelGrid ? snapPointToPixelGrid(handles.toHandle) : handles.toHandle,
    };
    viewStore.setEdgeBendPreview(nextPreview);
    return;
  }

  if (drag.mode === "resize" && drag.selectionBounds && drag.handle) {
    const resizedBounds = event.altKey
      ? resizeBoundsAroundCenter(drag.selectionBounds, drag.handle, dx, dy)
      : resizeBounds(drag.selectionBounds, drag.handle, dx, dy);
    const nextGroupBounds = event.shiftKey
      ? event.altKey
        ? constrainResizeBoundsAspectRatioAroundCenter(drag.selectionBounds, resizedBounds, drag.handle)
        : constrainResizeBoundsAspectRatio(drag.selectionBounds, resizedBounds, drag.handle)
      : resizedBounds;
    drag.ignoreChildConstraints = event.metaKey || event.ctrlKey;
    const nextPreviews = drag.nodeIds.flatMap(nodeId => {
      const bounds = drag.startBoundsById.get(nodeId);
      return bounds ? [{ nodeId, bounds: transformBoundsInGroup(bounds, drag.selectionBounds!, nextGroupBounds) }] : [];
    });
    viewStore.setPreviews(nextPreviews);
    return;
    return;
  }

  if (drag.mode === "marquee" || drag.mode === "vectorMarquee" || drag.mode === "animationMarquee") {
    const rect = normalizeRect({
      x: drag.startDocumentX,
      y: drag.startDocumentY,
      width: point.x - drag.startDocumentX,
      height: point.y - drag.startDocumentY,
    });
    const nextPreviews = [{ nodeId: "__marquee__", bounds: rect }];
    viewStore.setPreviews(nextPreviews);
    return;
  }
}

function handlePointerUpImpl(event: React.PointerEvent<Element>): void {
  if (host.sampledPointerIdRef.current === event.pointerId) {
    host.sampledPointerIdRef.current = undefined;
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  const drag = host.dragRef.current;
  if (!drag) {
    return;
  }

  host.dragRef.current = null;
  host.stopMoveAutopan();
  timeDebugStep("clearPointerHover", "input", "pointerup", () => clearPointerHover());
  host.setActiveImageHandle(undefined);
  host.setObjectSnapGuides([]);

  if (drag.mode === "pan") {
    host.commitCameraGesture();
    host.syncCanvasCursor();
    return;
  }

  if (drag?.mode === "guideCreate" || drag?.mode === "guideMove") {
    const canvas = host.canvasRef.current;
    const insets = host.visibleViewportInsets ?? { top: 0, left: 0, right: 0, bottom: 0 };
    if (canvas && drag.guideOrientation) {
      const rect = canvas.getBoundingClientRect();
      const overSourceRuler = drag.guideOrientation === "horizontal"
        ? pointerInHorizontalRuler(event.clientY, rect.top, insets.top, 20)
        : pointerInVerticalRuler(event.clientX, rect.left, insets.left, 20);
      if (drag.mode === "guideMove" && overSourceRuler && drag.guideSelection) {
        host.documentActions.onGuideDelete(drag.guideSelection);
        host.documentActions.onGuideSelectionChange(undefined);
      } else if (drag.mode === "guideCreate" && pointInsideCanvasContentArea(event.clientX, event.clientY, canvas, insets)) {
        const point = screenToDocument(event.clientX, event.clientY, canvas, host.cameraRef.current);
        const placement = resolveGuidePlacement(documentStore.getDocument(), host.resolvedActivePageId, drag.guideOrientation, point);
        if (placement) {
          host.documentActions.onGuideAdd(placement.scope, placement.guide);
          const selection = { id: placement.guide.id, scope: placement.scope };
          host.setActiveGuideSelection(selection);
          host.documentActions.onGuideSelectionChange(selection);
        }
      } else if (
        drag.mode === "guideMove" &&
        drag.moved &&
        drag.guideSelection &&
        drag.guideStartPosition !== undefined
      ) {
        const point = screenToDocument(event.clientX, event.clientY, canvas, host.cameraRef.current);
        const documentPosition = guideDocumentPositionFromPointer(drag.guideOrientation, point);
        const nextScope = resolveGuideScopeAtPoint(documentStore.getDocument(), host.resolvedActivePageId, point);
        if (nextScope) {
          const nextPosition = guidePositionForScope(documentStore.getDocument(), nextScope, drag.guideOrientation, documentPosition);
          if (guideScopesEqual(drag.guideSelection.scope, nextScope)) {
            if (nextPosition !== drag.guideStartPosition) {
              host.documentActions.onGuideMove(drag.guideSelection, drag.guideStartPosition, nextPosition);
            }
          } else {
            host.documentActions.onGuideRelocate(drag.guideSelection, drag.guideStartPosition, nextScope, nextPosition);
            host.setActiveGuideSelection({ id: drag.guideSelection.id, scope: nextScope });
          }
        }
      } else if (drag.mode === "guideMove" && !drag.moved && drag.guideSelection) {
        host.setActiveGuideSelection(drag.guideSelection);
        host.documentActions.onGuideSelectionChange(drag.guideSelection);
      }
    }
    host.setGuidePreview(undefined);
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag?.mode === "textSelect") {
    host.textInputRef.current?.focus();
    setTextareaSelection(host.textInputRef.current, host.textSelectionRef.current.anchor, host.textSelectionRef.current.focus);
    return;
  }

  if (drag?.mode === "animationObjectLink" && host.canvasRef.current) {
    const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
    host.setPrototypeLinkPreview(undefined);
    host.setAnimObjectDragGhost(undefined);

    if (drag.moved && drag.animationSourceNodeId && host.animationGraph) {
      const endpoint = (host.animationActions.onAnimationObjectConnect || host.animationActions.onAnimationTargetsConnect)
        ? nearestAnimationObjectInputPin(host.animationGraph.nodes, point) : undefined;

      if (host.vectorTargetNodeId && drag.animationSourceNodeId === host.vectorTargetNodeId) {
        const selection = selectionStore.getSelection();
        const vectorTargets = animationTargetsFromVectorSelection(host.vectorTargetNodeId, selection);

        if (endpoint && (host.animationActions.onAnimationTargetsConnect || host.animationActions.onAnimationObjectConnect)) {
          if (vectorTargets.length > 0 && host.animationActions.onAnimationTargetsConnect) {
            host.animationActions.onAnimationTargetsConnect(vectorTargets, endpoint);
          } else if (host.animationActions.onAnimationObjectConnect) {
            host.animationActions.onAnimationObjectConnect(drag.animationSourceNodeId, endpoint);
          }
          host.syncOverlayRender(undefined, { immediate: true });
          return;
        } else if (!endpoint) {
          if (vectorTargets.length > 0 && host.animationActions.onAnimationTargetsCreateNode) {
            host.animationActions.onAnimationTargetsCreateNode(vectorTargets, point);
          } else if (host.animationActions.onAnimationObjectCreateNode) {
            host.animationActions.onAnimationObjectCreateNode(drag.animationSourceNodeId, point);
          }
          host.syncOverlayRender(undefined, { immediate: true });
          return;
        }
      } else {
        if (endpoint && host.animationActions.onAnimationObjectConnect) {
          host.animationActions.onAnimationObjectConnect(drag.animationSourceNodeId, endpoint);
          host.syncOverlayRender(undefined, { immediate: true });
          return;
        } else if (!endpoint) {
          const source = findSceneNode(documentStore.getDocument(), drag.animationSourceNodeId);
          if (source?.type !== "frame" && host.selectedNodeIds.length > 1 && host.animationActions.onAnimationObjectsCreateGroupNode) {
            host.animationActions.onAnimationObjectsCreateGroupNode(host.selectedNodeIds, point);
          } else if (host.animationActions.onAnimationObjectCreateNode) {
            host.animationActions.onAnimationObjectCreateNode(drag.animationSourceNodeId, point);
          }
          host.syncOverlayRender(undefined, { immediate: true });
          return;
        }
      }
    }

    if (!drag.moved && drag.animationSourceNodeId && !host.vectorTargetNodeId) {
      if (!event.shiftKey) {
        host.documentActions.onSelectionChange([drag.animationSourceNodeId], drag.animationSourceNodeId);
      }
    }
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag?.mode === "animationMarquee") {
    const marquee = host.previews[0]?.bounds;
    if (host.vectorTargetNodeId && drag.moved && marquee) {
      applyVectorAreaSelection(
        host,
        resolveVectorAreaSelection(documentStore.getDocument(), host.vectorTargetNodeId, { type: "rect", rect: marquee }),
        event.shiftKey,
        "merge",
      );
    } else if (!host.vectorTargetNodeId) {
      if (drag.moved && marquee && host.animationGraph && host.animationActions.onAnimationNodeSelectionChange) {
        const marqueeSelection = animationNodesInRect(host.animationGraph.nodes, marquee);
        const nextSelection = event.shiftKey ? mergeSelection(host.selectedAnimationNodeIds, marqueeSelection) : marqueeSelection;
        host.animationActions.onAnimationNodeSelectionChange(nextSelection, nextSelection.at(-1));
      } else if (!event.shiftKey && host.animationActions.onAnimationNodeSelectionChange) {
        host.animationActions.onAnimationNodeSelectionChange([], undefined);
      }
    }
    viewStore.setPreviews([]);
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag?.mode === "prototypeLink" && host.canvasRef.current) {
    const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
    host.setPrototypeLinkPreview(undefined);
    if (drag.moved && drag.prototypeSourceNodeId) {
      const targetFrame = findTopFrameAtPoint(documentStore.getDocument(), point, host.resolvedActivePageId);
      if (targetFrame && targetFrame.id !== drag.prototypeSourceNodeId) {
        host.documentActions.onPrototypeLinkCreate(drag.prototypeSourceNodeId, targetFrame.id);
        return;
      }
    }

    if (!drag.moved && drag.prototypeSourceNodeId) {
      host.documentActions.onSelectionChange([drag.prototypeSourceNodeId], drag.prototypeSourceNodeId);
    }
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag?.mode === "shapeBuilder" && drag.nodeIds[0]) {
    const selection = {
      addedRegionIds: [...host.shapeBuilderAddedRegionIdsRef.current],
      removedRegionIds: [...host.shapeBuilderRemovedRegionIdsRef.current],
    };
    host.resetShapeBuilderSelection();
    if (selection.addedRegionIds.length > 0 || selection.removedRegionIds.length > 0) {
      host.vectorActions.onShapeBuilderCommit(drag.nodeIds[0], selection);
    }
    return;
  }

  if (!drag?.moved && host.canvasRef.current) {
    if (drag?.mode === "textDraw") {
      host.documentActions.onTextInsert({ x: drag.startDocumentX, y: drag.startDocumentY });
      requestAnimationFrame(() => host.textInputRef.current?.focus());
      return;
    }

    if (drag?.mode === "draw") {
      host.setDraftShape(undefined);
      host.syncOverlayRender({ draftShape: undefined }, { immediate: true });
      return;
    }

    if (drag?.mode === "pencil") {
      host.clearDraftPencilRender();
      return;
    }

    if (drag?.mode === "lasso") {
      host.syncOverlayRender(undefined, { immediate: true });
      return;
    }

    if (drag?.mode === "move" && drag.deselectOnClick) {
      host.documentActions.onSelectionChange([], undefined);
      return;
    }

    if (drag?.mode === "move" || drag?.mode === "resize" || drag?.mode === "rotate" || drag?.mode === "origin" || drag?.mode === "vectorTransformResize" || drag?.mode === "vectorTransformRotate") {
      return;
    }

    if (drag?.mode === "vertex" || drag?.mode === "edge" || drag?.mode === "extrude" || drag?.mode === "handle" || drag?.mode === "knifeLine" || drag?.mode === "knifeAround" || drag?.mode === "edgeBend" || drag?.mode === "pathInsert" || drag?.mode === "penCurve" || drag?.mode === "sculpt" || drag?.mode === "gradient" || drag?.mode === "imageFill" || drag?.mode === "regionFill") {
      host.setKnifePreview(undefined);
      return;
    }

    if (drag?.mode === "vectorMarquee") {
      if (drag.exitVectorModeOnClick) {
        host.vectorActions.onExitVectorMode();
      }
      return;
    }

    const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
    const hit = timeDebugStep("hitTestDocument", "input", "pointerup-click", () =>
      hitTestDocument(documentStore.getDocument(), point, host.selectionBounds, host.cameraRef.current.zoom, host.hitTestOptions({
        deepSelect: deepSelectModifier(event),
        selectedNodeIds: host.editableSelectedNodeIds,
      })));
    if (hit.type === "node") {
      host.documentActions.onPrototypeLinkSelect(undefined);
      const nextSelection = applySelectionClick(host.selectedNodeIds, hit.nodeId, event.shiftKey);
      timedSelectionChange(nextSelection, nextSelection.includes(hit.nodeId) ? hit.nodeId : nextSelection.at(-1));
    } else if (!event.shiftKey) {
      host.setActiveGuideSelection(undefined);
      host.documentActions.onGuideSelectionChange(undefined);
      host.documentActions.onPrototypeLinkSelect(undefined);
      timedSelectionChange([], undefined);
    }
    return;
  }

  if (drag?.mode === "move" && drag.flexReorderTarget && drag.nodeIds[0] && host.previews.length > 0 && !event.altKey) {
    host.documentActions.onFlexReorderCommit(drag.nodeIds[0], drag.flexReorderTarget.parentFrameId, drag.flexReorderTarget.index);
    viewStore.setPreviews([]);
    return;
  }

  if (drag?.mode === "move" && host.previews.length > 0 && event.altKey) {
    const offset = dragOffsetFromPreview(drag, host.previews);
    if (offset) {
      const dropPoint = host.canvasRef.current
        ? screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current)
        : undefined;
      host.documentActions.onDuplicateDrag(drag.nodeIds, offset, dropPoint);
    }
    viewStore.setPreviews([]);
    return;
  }

  if ((drag?.mode === "move" || drag?.mode === "resize") && host.previews.length > 0) {
    const dropPoint = drag.mode === "move" && host.canvasRef.current
      ? screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current)
      : undefined;
    host.documentActions.onBoundsCommit(
      host.previews.map(preview => ({ nodeId: preview.nodeId, bounds: preview.bounds })),
      {
        dropPoint,
        ignoreChildConstraints: drag.mode === "resize" ? drag.ignoreChildConstraints : undefined,
      },
    );
    viewStore.setPreviews([]);
    return;
  }

  if (drag?.mode === "rotate" && host.previews.length > 0) {
    host.documentActions.onTransformCommit(
      host.previews.flatMap(preview => {
        const startTransform = drag.startTransformById?.get(preview.nodeId);
        const bounds = drag.startBoundsById.get(preview.nodeId);
        return startTransform && preview.rotation !== undefined
          ? [{
              nodeId: preview.nodeId,
              transform: {
                ...startTransform,
                ...(startTransform.rotation === 0 && startTransform.x === 0 && startTransform.y === 0 && bounds ? rectCenter(bounds) : {}),
                rotation: preview.rotation,
              },
            }]
          : [];
      }),
    );
    if (drag.nodeIds.length === 1) {
      host.setVisibleRotationOriginNodeId(drag.nodeIds[0]);
    }
    viewStore.setPreviews([]);
    return;
  }

  if (drag?.mode === "origin" && drag.nodeIds[0] && host.originPreview?.nodeId === drag.nodeIds[0]) {
    host.documentActions.onRotationOriginCommit(drag.nodeIds[0], { x: host.originPreview.x, y: host.originPreview.y });
    host.setOriginPreview(undefined);
    host.setVisibleRotationOriginNodeId(drag.nodeIds[0]);
    return;
  }

  if ((drag?.mode === "vectorTransformResize" || drag?.mode === "vectorTransformRotate") && drag.nodeIds[0] && drag.vertexId && host.vertexPreview) {
    host.vectorActions.onVertexCommit(drag.nodeIds[0], drag.vertexId, host.vertexPreview.x, host.vertexPreview.y, host.vertexPreview.vertices, host.vertexPreview.handles);
    viewStore.setVertexPreview(undefined);
    return;
  }

  if (drag?.mode === "vertex" && drag.vertexId && drag.nodeIds[0] && host.vertexPreview) {
    host.vectorActions.onVertexCommit(drag.nodeIds[0], drag.vertexId, host.vertexPreview.x, host.vertexPreview.y, host.vertexPreview.vertices);
    viewStore.setVertexPreview(undefined);
    return;
  }

  if (drag?.mode === "edge" && drag.nodeIds[0] && drag.edgeIds?.length && host.vertexPreview?.vertices?.length) {
    host.vectorActions.onEdgeMoveCommit(drag.nodeIds[0], drag.edgeIds, host.vertexPreview.vertices);
    viewStore.setVertexPreview(undefined);
    return;
  }

  if (drag?.mode === "extrude") {
    const preview = viewStore.vectorTopologyPreview.getValue();
    if (
      drag.moved
      && drag.nodeIds[0]
      && preview
      && preview.nodeId === drag.nodeIds[0]
      && (preview.newEdgeIds?.length ?? 0) > 0
      && ((drag.lastDx ?? 0) !== 0 || (drag.lastDy ?? 0) !== 0)
    ) {
      host.vectorActions.onExtrudeCommit(drag.nodeIds[0], preview.network, preview.newEdgeIds ?? [], preview.newVertexIds);
    }
    viewStore.setVectorTopologyPreview(undefined);
    return;
  }

  if (drag?.mode === "handle" && drag.edgeId && drag.endpoint && drag.nodeIds[0] && host.handlePreview) {
    host.vectorActions.onHandleCommit(drag.nodeIds[0], drag.edgeId, drag.endpoint, host.handlePreview.x, host.handlePreview.y);
    viewStore.setHandlePreview(undefined);
    return;
  }

  if (drag?.mode === "edgeBend" && drag.edgeId && drag.nodeIds[0] && host.edgeBendPreview) {
    host.vectorActions.onEdgeBendCommit(drag.nodeIds[0], drag.edgeId, {
      fromHandle: host.edgeBendPreview.fromHandle,
      toHandle: host.edgeBendPreview.toHandle,
    });
    viewStore.setEdgeBendPreview(undefined);
    return;
  }

  if (drag?.mode === "sculpt") {
    if (drag.nodeIds[0] && host.vertexPreview?.vertices?.length) {
      host.vectorActions.onSculptCommit(drag.nodeIds[0], host.vertexPreview.vertices);
    }
    viewStore.setVertexPreview(undefined);
    viewStore.setSculptBrushPreview(undefined);
    return;
  }

  if (drag?.mode === "penCurve" && drag.edgeId && drag.nodeIds[0] && host.edgeBendPreview) {
    host.vectorActions.onEdgeBendCommit(drag.nodeIds[0], drag.edgeId, {
      fromHandle: host.edgeBendPreview.fromHandle,
      toHandle: host.edgeBendPreview.toHandle,
    });
    viewStore.setEdgeBendPreview(undefined);
    return;
  }

  if (drag?.mode === "pathInsert") {
    return;
  }

  if (drag?.mode === "gradient") {
    return;
  }

  if (drag?.mode === "imageFill") {
    return;
  }

  if (drag?.mode === "regionFill") {
    return;
  }

  if (drag?.mode === "textDraw" && host.canvasRef.current && drag.moved) {
    const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
    const editPoint = host.snapToPixelGrid ? snapPointToPixelGrid(point) : point;
    const bounds = shapeDrawBounds(
      { x: drag.startDocumentX, y: drag.startDocumentY },
      editPoint,
      "rectangle",
      false,
      false,
      host.snapToPixelGrid,
      "whole",
    );
    if (isDrawableBounds("rectangle", bounds)) {
      host.documentActions.onTextInsert({ x: bounds.x, y: bounds.y }, bounds);
    } else {
      host.documentActions.onTextInsert({ x: drag.startDocumentX, y: drag.startDocumentY });
    }
    host.setDraftShape(undefined);
    host.syncOverlayRender({ draftShape: undefined }, { immediate: true });
    requestAnimationFrame(() => host.textInputRef.current?.focus());
    return;
  }

  if (drag?.mode === "draw" && drag.shapeKind && host.canvasRef.current) {
    const point = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
    const editPoint = host.snapToPixelGrid ? snapPointToPixelGrid(point) : point;
    const { bounds } = shapeDrawBoundsForDrag(drag, editPoint, event.shiftKey, event.altKey);
    if (isDrawableBounds(drag.shapeKind, bounds)) {
      if (drag.shapeKind === "frame") {
        const parentFrame = findDeepestFrameAtPoint(documentStore.getDocument(), { x: drag.startDocumentX, y: drag.startDocumentY }, host.resolvedActivePageId);
        host.documentActions.onFrameDraw(bounds, parentFrame?.id);
      } else {
        const startTopFrame = findTopFrameAtPoint(documentStore.getDocument(), { x: drag.startDocumentX, y: drag.startDocumentY }, host.resolvedActivePageId);
        const endTopFrame = findTopFrameAtPoint(documentStore.getDocument(), editPoint, host.resolvedActivePageId);
        // If the draw gesture spans two different top-level frames, place as their page-level
        // sibling (z-top) instead of inside the start frame. If both endpoints are in the same
        // top-level frame (or one is nested inside the other), use normal placement.
        const parentFrame = startTopFrame?.id === endTopFrame?.id ? startTopFrame : undefined;
        host.documentActions.onShapeDraw(drag.shapeKind, bounds, parentFrame?.id);
      }
    }
    host.setDraftShape(undefined);
    host.syncOverlayRender({ draftShape: undefined }, { immediate: true });
    return;
  }

  if (drag?.mode === "pencil") {
    const points = drag.pencilPoints ?? [];
    host.clearDraftPencilRender();
    if (points.length >= 2) {
      host.vectorActions.onPencilDraw(points);
    }
    return;
  }

  if (drag?.mode === "lasso") {
    const lasso = drag.lassoPoints ?? [];
    const vectorNodeId = host.vectorTargetNodeId ?? (host.mode === "vectorEdit" ? host.primarySelectedNodeId : undefined);
    if (vectorNodeId) {
      applyVectorAreaSelection(
        host,
        resolveVectorAreaSelection(documentStore.getDocument(), vectorNodeId, { type: "lasso", points: lasso }),
        event.shiftKey,
        "toggle",
      );
    } else {
      const lassoSelection = getNodesInLasso(documentStore.getDocument(), lasso, host.resolvedActivePageId);
      const nextSelection = event.shiftKey ? toggleSelectionSet(host.selectedNodeIds, lassoSelection) : lassoSelection;
      host.documentActions.onSelectionChange(nextSelection, nextSelection.at(-1));
    }
    if (!event.shiftKey) {
      host.onToolChange("select");
    }
    host.syncOverlayRender(undefined, { immediate: true });
    return;
  }

  if (drag?.mode === "knifeLine" && drag.nodeIds[0] && drag.knifePoints?.length) {
    const points = drag.knifePoints;
    const cuts = points.length >= 2 ? knifeLineCuts(documentStore.getDocument(), drag.nodeIds[0], points[0]!, points[points.length - 1]!) : [];
    host.setKnifePreview(undefined);
    host.vectorActions.onKnifeCutEdges(drag.nodeIds[0], cuts);
    return;
  }

  if (drag?.mode === "knifeAround" && drag.nodeIds[0] && drag.vertexId && drag.knifePoints?.length) {
    const currentEdgeIds = knifeAroundEdgeIds(documentStore.getDocument(), drag.nodeIds[0], drag.vertexId, drag.knifePoints);
    const pendingEdgeIds = host.pendingKnifeAroundCutRef.current?.nodeId === drag.nodeIds[0] && host.pendingKnifeAroundCutRef.current.vertexId === drag.vertexId
      ? host.pendingKnifeAroundCutRef.current.edgeIds
      : [];
    const edgeIds = mergeSelection(pendingEdgeIds, currentEdgeIds);
    if (event.shiftKey) {
      host.setPendingKnifeAroundCut({ nodeId: drag.nodeIds[0], vertexId: drag.vertexId, edgeIds });
      host.setKnifePreview({ points: [], edgeIds, mode: "around" });
      return;
    }

    host.setPendingKnifeAroundCut(undefined);
    host.setKnifePreview(undefined);
    host.vectorActions.onKnifeCutAroundVertex(drag.nodeIds[0], drag.vertexId, edgeIds);
    return;
  }

  if (drag?.mode === "marquee" && host.previews[0]) {
    const marqueeSelection = timeDebugStep("getNodesInMarquee", "input", "pointerup-marquee", () =>
      getNodesInMarquee(documentStore.getDocument(), host.previews[0]!.bounds, host.resolvedActivePageId));
    const nextSelection = event.shiftKey ? mergeSelection(host.selectedNodeIds, marqueeSelection) : marqueeSelection;
    timedSelectionChange(nextSelection, nextSelection.at(-1));
    viewStore.setPreviews([]);
    return;
  }

  if (drag?.mode === "vectorMarquee" && host.previews[0] && host.primarySelectedNodeId) {
    applyVectorAreaSelection(
      host,
      resolveVectorAreaSelection(documentStore.getDocument(), host.primarySelectedNodeId, { type: "rect", rect: host.previews[0].bounds }),
      event.shiftKey,
      "merge",
    );
    viewStore.setPreviews([]);
  }
}

function handlePointerCancelImpl(): void {
  const drag = host.dragRef.current;
  host.dragRef.current = null;
  host.stopMoveAutopan();
  if (drag?.mode === "pan") {
    host.commitCameraGesture();
    host.syncCanvasCursor();
  }
  if (drag?.mode === "vectorTransformRotate" && host.vectorSmartTransformKey) {
    host.setVectorSmartTransform({ key: host.vectorSmartTransformKey, rotation: drag.startSmartTransformRotation ?? 0 });
  }
  viewStore.setPreviews([]);
  host.setObjectSnapGuides([]);
  viewStore.setVertexPreview(undefined);
  viewStore.setHandlePreview(undefined);
  viewStore.setEdgeBendPreview(undefined);
  viewStore.setSculptBrushPreview(undefined);
  viewStore.setVectorTopologyPreview(undefined);
  host.setDraftShape(undefined);
  host.syncOverlayRender({ previews: [], objectSnapGuides: [], draftShape: undefined }, { immediate: true });
  host.clearDraftPencilRender();
  clearPointerHover();
  host.setActiveImageHandle(undefined);
  host.setOriginPreview(undefined);
  host.setKnifePreview(undefined);
  host.setColorSamplePreview(undefined);
  host.setPrototypeLinkPreview(undefined);
  host.setAnimObjectDragGhost(undefined);
}

function shapeDrawBoundsForDrag(
  drag: DragState,
  editPoint: { x: number; y: number },
  constrained: boolean,
  centered: boolean,
): { bounds: Rect; guides: ObjectSnapGuide[] } {
  const bounds = shapeDrawBounds(
    { x: drag.startDocumentX, y: drag.startDocumentY },
    editPoint,
    drag.shapeKind ?? "rectangle",
    constrained,
    centered,
    host.snapToPixelGrid,
    "whole",
  );
  if (drag.shapeKind === "frame" || !host.snapToObjects) {
    return { bounds, guides: [] };
  }

  const parentFrame = findTopFrameAtPoint(documentStore.getDocument(), { x: drag.startDocumentX, y: drag.startDocumentY }, host.resolvedActivePageId);
  return parentFrame?.type === "frame"
    ? snapShapeDrawBoundsToFrame(bounds, parentFrame.bounds, host.cameraRef.current.zoom)
    : { bounds, guides: [] };
}

function handleFrameLabelPointerDown(event: React.PointerEvent<HTMLCanvasElement>, frame: FrameLabelOverlay): void {
  if (!host.canvasRef.current || event.button !== 0) {
    return;
  }

  const scriptAction = hitTestFrameLabelScriptAction(frame, event.clientX, event.clientY);
  if (scriptAction) {
    event.preventDefault();
    event.stopPropagation();
    host.setRenamingFrameId(undefined);
    if (scriptAction === "edit") {
      host.documentActions.onEditFrameScript?.(frame.id);
    } else {
      host.documentActions.onRunFrameScript?.(frame.id);
    }
    return;
  }

  if (isNodeLockedForEditing(documentStore.getDocument(), frame.id)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  host.setRenamingFrameId(undefined);

  const nextSelection = applySelectionPointerDown(host.selectedNodeIds, frame.id, event.shiftKey);
  host.documentActions.onSelectionChange(nextSelection, nextSelection.includes(frame.id) ? frame.id : nextSelection.at(-1));
  host.syncOverlayRender(
    { selectedNodeIds: editableNodeIds(documentStore.getDocument(), nextSelection) },
    { immediate: true },
  );

  if (!nextSelection.includes(frame.id)) {
    return;
  }

  event.currentTarget.setPointerCapture(event.pointerId);
  const activeNodeIds = nextSelection;
  const boundsById = new Map(
    getAllSceneNodes(documentStore.getDocument())
      .filter(node => activeNodeIds.includes(node.id))
      .map(node => [node.id, node.bounds] as const),
  );
  const transformById = new Map(
    getAllSceneNodes(documentStore.getDocument())
      .filter(node => activeNodeIds.includes(node.id))
      .map(node => [node.id, node.transform] as const),
  );
  const startPoint = screenToDocument(event.clientX, event.clientY, host.canvasRef.current, host.cameraRef.current);
  host.dragRef.current = {
    mode: "move",
    startClientX: event.clientX,
    startClientY: event.clientY,
    startDocumentX: startPoint.x,
    startDocumentY: startPoint.y,
    startBoundsById: boundsById,
    startTransformById: transformById,
    selectionBounds: selectionBoundsForTransforms(documentStore.getDocument(), activeNodeIds, []),
    nodeIds: activeNodeIds,
    objectSnapCandidates: visibleObjectSnapCandidates(documentStore.getDocument(), activeNodeIds, canvasViewportBounds(host.canvasRef.current, host.cameraRef.current)),
    deselectOnClick: !event.shiftKey && host.selectedNodeIds.includes(frame.id),
    moved: false,
  };
  host.moveAutopanPointerRef.current = {
    clientX: event.clientX,
    clientY: event.clientY,
    pointerId: event.pointerId,
    altKey: event.altKey,
    shiftKey: event.shiftKey,
  };
  host.startMoveAutopan();
  host.syncCanvasCursor();
}

function handlePointerLeave(): void {
  host.clearPointerStatus();
  host.setPointerHover({
    hoveredNodeId: undefined,
    hoveredFrameLabelId: undefined,
    hoveredRotationHandle: undefined,
    hoveredRotationOrigin: false,
    pathPreviewPoint: undefined,
    pathPreviewSegment: undefined,
    hoveredPrototypeNodeId: undefined,
  });
  host.setColorSamplePreview(undefined);
  if (host.dragRef.current?.mode !== "sculpt") {
    viewStore.setSculptBrushPreview(undefined);
  }
}

function updateDoubleClickBaseGroup(event: React.PointerEvent<HTMLCanvasElement>): boolean {
  const previous = host.lastPointerDownForDoubleClickRef.current;
  const isSameClickSequence = previous
    ? performance.now() - previous.time <= 500 && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <= 6
    : false;

  host.lastPointerDownForDoubleClickRef.current = {
    time: performance.now(),
    x: event.clientX,
    y: event.clientY,
  };
  return isSameClickSequence;
}

function cancelAnimationObjectLinkDrag(): boolean {
  if (host.dragRef.current?.mode !== "animationObjectLink") {
    return false;
  }

  host.dragRef.current = null;
  host.setPrototypeLinkPreview(undefined);
  host.setAnimObjectDragGhost(undefined);
  clearPointerHover();
  host.syncOverlayRender(undefined, { immediate: true });
  return true;
}

  return {
    handlePointerDown: (event: React.PointerEvent<HTMLCanvasElement>) => {
      beginPointerDownTrace();
      try {
        handlePointerDownImpl(event);
      } finally {
        endPointerDownTrace(`mode=${host.mode} tool=${host.activeTool}`);
      }
    },
    handlePointerMove: (event: React.PointerEvent<Element>) => {
      beginPointerMoveTrace();
      try {
        handlePointerMoveImpl(event);
      } finally {
        endPointerMoveTrace(`mode=${host.mode} tool=${host.activeTool}`);
      }
    },
    handlePointerUp: (event: React.PointerEvent<Element>) => {
      const dragMode = host.dragRef.current?.mode ?? "none";
      beginPointerUpTrace();
      try {
        handlePointerUpImpl(event);
      } finally {
        endPointerUpTrace(`mode=${host.mode} tool=${host.activeTool} drag=${dragMode}`);
      }
    },
    handlePointerCancel: () => {
      beginPointerCancelTrace();
      try {
        handlePointerCancelImpl();
      } finally {
        endPointerCancelTrace(`mode=${host.mode} tool=${host.activeTool}`);
      }
    },
    handlePointerLeave,
    clearPointerHover,
    handleFrameLabelPointerDown,
    updateDoubleClickBaseGroup,
    cancelAnimationObjectLinkDrag,
  };
}
